/**
 * /backgammon — the page: set-up, the game loop for the three ways to play
 * (against the computer, two people on one screen, by link), the board, the
 * rail panels, saving across a reload, and sizing the console to the screen.
 *
 * Rules live in rules.js, the match in game.js, the computer in bot.js (run in
 * bot-worker.js), the board in board.js, the room in online.js. This file only
 * decides WHO acts next and what the screen says.
 */
import * as R from './rules.js';
import * as G from './game.js';
import { createBoard } from './board.js';
import * as F from './fair-dice.js';

/* Loaded on demand: a game against the computer never needs the room code, and
   every module of a static import graph is one more request the whole page
   depends on (one failed fetch and nothing runs). */
let onlineMod = null;
function loadOnline() {
    if (!onlineMod) onlineMod = import('./online.js').catch((err) => { onlineMod = null; throw err; });
    return onlineMod;
}
let sndMod = null, sndLoading = null;
function loadSound() {
    if (!sndLoading) {
        sndLoading = import('./sound.js')
            .then((m) => { sndMod = m; m.setEnabled(settings.sound); return m; })
            .catch(() => { sndLoading = null; return null; });
    }
    return sndLoading;
}
const Snd = {
    play(kind) {
        if (!settings.sound) return;
        if (sndMod) { sndMod.play(kind); return; }
        loadSound().then((m) => { if (m) m.play(kind); });
    },
    setEnabled(on) { if (sndMod) sndMod.setEnabled(on); },
};

const CFG = window.__BG_CFG || { lang: {}, locale: 'en' };
const SLUG = 'backgammon';
const SAVE_KEY = 'bg:save:v1';
const $ = (id) => document.getElementById(id);
const reduceMotion = () => window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function L(k, vars) {
    let s = CFG.lang[k];
    if (s == null) s = k;
    // longer names first: «:now» must not be eaten by «:n»
    if (vars) for (const n of Object.keys(vars).sort((a, b) => b.length - a.length)) s = s.split(':' + n).join(vars[n]);
    return s;
}
function toast(msg, type) { try { window.__toast ? window.__toast(msg, type || 'info') : null; } catch (_) {} }
function stage(n, d) { try { window.__stage && window.__stage(n, d || {}); } catch (_) {} }
function diag(issue, d) { try { window.__aeDiagnostic && window.__aeDiagnostic(Object.assign({ issue }, d || {})); } catch (_) {} }
function feature(action, d) { try { window.__ae && window.__ae('feature_use', Object.assign({ action }, d || {})); } catch (_) {} }

/* ─────────────────────────── settings and set-up ─────────────────────────── */

const TS = window.__toolSettings;
const load = (k, fb) => { try { return TS ? TS.load(SLUG, k, fb) : fb; } catch (_) { return fb; } };
const save = (k, v) => { try { TS && TS.save(SLUG, k, v); } catch (_) {} };

const THEMES = ['wood', 'felt', 'night', 'marble'];
const settings = Object.assign({
    sound: true, autoRoll: false, autoConfirm: false, autoForced: true,
    numbers: true, mirror: false, rotate: true, animate: true, lastArrows: true, theme: 'wood', meter: false,
}, load('settings', {}));
if (!THEMES.includes(settings.theme)) settings.theme = 'wood';
const defVariant = CFG.defaultVariant || 'long';
// the defaults live with the pre-paint script of the blade (it draws the set-up before this module runs)
const setup = Object.assign({
    variant: defVariant, mode: 'bot', level: 'medium', matchTo: 3, cube: false, crawford: true, koksLong: false,
}, window.__bgSetupDefaults || {}, load('setup', {}));
Snd.setEnabled(settings.sound);

/* ─────────────────────────────── app state ──────────────────────────────── */

const app = {
    mode: null,          // 'bot' | 'local' | 'online'
    level: 'medium',
    me: 0,               // the person's side (bot: always White; online: host White, guest Black)
    m: null,             // the match (game.js)
    turn: null,          // the live turn (rules.createTurn) while a person moves
    sel: null,           // selected source point (mover's numbers)
    busy: false,         // animation / computer thinking
    rolling: null,       // dice faces flying during the throw animation
    hint: null,
    last: null, lastBy: 0,
    net: null,           // online session
    netRole: null, netCode: null,
    netState: '',
    firstMoveSent: false,
    overlay: null,       // {kind:'cube'|'result'|'confirm', ...}
    msg: '', msgKind: '',
    replay: null,        // index into history while a past position is shown
    review: null,        // the analysis of the finished game (analysis.js report)
    reviewing: false,
    meter: null,         // the viewer's win chance by the engine (a setting)
    fair: null,          // {seed, commit, log:[[d1,d2]…], verified:[…]} — dice nobody can change afterwards
    tab: 'game',
};

let board = null;
let worker = null, workerSeq = 0;
const workerWait = new Map();

/* ─────────────────────────────── helpers ────────────────────────────────── */

const g = () => app.m && app.m.game;
/* Every await hands the page back to the person: «New game», «Resign», a load
   or the other side's sync can replace the game a continuation was about.
   Before 16.10 the bot's move went on with the NEW match — `G.turnFor()` /
   `G.plays()` answered null (Sentry TIMBRICA-FRONTEND-Q4, PY) — and `app.busy`
   stayed true for good: every later step() bailed, the board froze.
   A continuation that finds its game gone lets go of `busy` and restarts the
   step machine for whatever is on the board now. */
const sameGame = (gm, phase) => g() === gm && (!phase || gm.phase === phase);
function letGo() { app.busy = false; step(); }
const ovOpen = () => !!(app.overlay && app.overlay.kind !== 'closed');
function isHuman(p) {
    if (!app.m) return false;
    if (app.mode === 'bot') return p === app.me;
    if (app.mode === 'local') return true;
    if (app.mode === 'online') return p === app.me;
    return false;
}
function viewer() {
    if (app.mode === 'local') {
        const t = g() ? g().turn : 0;
        return settings.rotate && t >= 0 ? t : 0;
    }
    return app.me;
}
function nameOf(p) {
    if (app.mode === 'bot') return p === app.me ? L('ui.you') : L('setup.mode_bot');
    if (app.mode === 'online') return p === app.me ? L('ui.you') : L('ui.opponent');
    return p === 0 ? L('ui.white') : L('ui.black');
}
function colorName(p) { return p === 0 ? L('ui.white') : L('ui.black'); }

/* ─────────────────────────────── the worker ─────────────────────────────── */

function ensureWorker() {
    if (worker !== null) return worker;
    // asked first (the layout's silent probe): an engine without module workers
    // (Firefox < 114, Safari < 15) is known before the worker is fetched — the bot
    // plays on the main thread from the first move instead of after a dead worker
    if (window.__moduleWorkerOk && !window.__moduleWorkerOk()) {
        diag('bot_worker_unavailable', { msg: 'module_worker' });
        worker = false;
        return worker;
    }
    try {
        // the URL comes from the page (AssetRouter::worker — versioned in the path,
        // so the worker and the modules it imports load from one release);
        // import.meta.url would not survive the deploy minifier here
        if (!CFG.workerUrl) throw new Error('no worker url');
        worker = new Worker(CFG.workerUrl, { type: 'module' });
        worker.onmessage = (e) => onBotReply(e.data || {});
        worker.onerror = (e) => {
            // handled: botAsk falls back to the main thread — so cancel the event.
            // Without preventDefault() an uncaught error inside the worker is ALSO
            // re-reported to the page's window.onerror: Safari 12 has no module
            // workers, runs the script as a classic one and dies on `import` —
            // the game went on, and Sentry got «SyntaxError: Unexpected token '{'.
            // import call expects exactly one argument.» (TIMBRICA-FRONTEND-NM).
            // Same move as chess-stockfish.js.
            if (e && typeof e.preventDefault === 'function') e.preventDefault();
            diag('bot_worker_error', { msg: String(e && e.message || '') });
            for (const w of workerWait.values()) w.reject(new Error('worker'));
            workerWait.clear();
            try { worker.terminate(); } catch (_) {}
            worker = false;
            netsReady = {};
        };
    } catch (err) {
        diag('bot_worker_unavailable', { msg: String(err && err.message || err) });
        worker = false;
    }
    return worker;
}

function onBotReply(d) {
    if (d.diag) { diag('bg_' + d.diag, { url: d.url, msg: d.msg }); return; }
    const w = workerWait.get(d.id);
    if (!w) return;
    if (d.progress != null && !d.done) { if (w.onProgress) w.onProgress(d.progress); w.touch(); return; }
    workerWait.delete(d.id);
    clearTimeout(w.timer);
    if (d.error) w.reject(new Error(d.error)); else w.resolve(d);
}

/* no module worker: the same answers on the main thread (slower, the game goes on) */
let coreLocal = null;
function localAsk(msg, o) {
    if (!coreLocal) coreLocal = import('./bot-core.js');
    return coreLocal.then((C) => new Promise((resolve, reject) => {
        C.handle(msg, (d) => {
            if (d.diag) { diag('bg_' + d.diag, { url: d.url, msg: d.msg }); return; }
            if (d.progress != null && !d.done) { if (o.onProgress) o.onProgress(d.progress); return; }
            if (d.error) reject(new Error(d.error)); else resolve(d);
        });
    }));
}

/**
 * Ask the computer. o.timeoutMs — silence allowed (progress messages reset
 * it), o.onProgress — for the long jobs (the review).
 */
async function botAsk(msg, o) {
    o = o || {};
    const w = ensureWorker();
    if (w) {
        try {
            return await new Promise((resolve, reject) => {
                const id = ++workerSeq;
                const ms = o.timeoutMs || 20000;
                const entry = { resolve, reject, onProgress: o.onProgress, timer: 0 };
                entry.touch = () => {
                    clearTimeout(entry.timer);
                    entry.timer = setTimeout(() => { if (workerWait.has(id)) { workerWait.delete(id); reject(new Error('timeout')); } }, ms);
                };
                workerWait.set(id, entry);
                entry.touch();
                w.postMessage(Object.assign({ id }, msg));
            });
        } catch (err) {
            diag('bot_worker_failed', { kind: msg.kind, msg: String(err && err.message || err) });
            if (msg.kind === 'analyze') throw err;
        }
    }
    if (msg.kind !== 'init' && !localNetsAsked) { localNetsAsked = true; await localAsk({ kind: 'init', nets: CFG.nets || {} }, {}).catch(() => null); }
    return localAsk(msg, o);
}
let localNetsAsked = false;

/* the nets load in the worker, per variant, the first time a game, a hint or the
   review of that variant needs them: a long-nardy player never downloads the
   750 KB of the short-game nets (and the first move of the computer waits for
   52 KB, not 800) */
let netsReady = {};
function ensureNets(v) {
    v = v || (app.m ? app.m.cfg.variant : setup.variant);
    if (!netsReady[v]) {
        netsReady[v] = botAsk({ kind: 'init', nets: CFG.nets || {}, variant: v }, { timeoutMs: 60000 })
            .then((r) => { app.nets = Object.assign({}, app.nets || { short: false, long: false }, (r && r.has) || {}); return app.nets; })
            .catch(() => app.nets || { short: false, long: false });
    }
    return netsReady[v];
}

/* ─────────────────────────────── saving ─────────────────────────────────── */

function persist() {
    try {
        if (!app.m) { localStorage.removeItem(SAVE_KEY); return; }
        const data = {
            v: 1, t: Date.now(), mode: app.mode, level: app.level, me: app.me, m: app.m,
            partial: app.turn ? app.turn.played.map((u) => [u.from, u.d]) : [],
            net: app.mode === 'online' ? { role: app.netRole, code: app.netCode } : null,
            fair: app.fair,
        };
        localStorage.setItem(SAVE_KEY, JSON.stringify(data));
    } catch (_) {}
}
function readSave() {
    try {
        const raw = localStorage.getItem(SAVE_KEY);
        if (!raw) return null;
        const d = JSON.parse(raw);
        if (!d || d.v !== 1) return null;
        const m = G.deserialize(d.m);
        if (!m) return null;
        d.m = m;
        return d;
    } catch (_) { return null; }
}
function dropSave() { try { localStorage.removeItem(SAVE_KEY); } catch (_) {} }

/* ─────────────────────────────── dice ───────────────────────────────────── */

/*
 * Dice that cannot be changed after the fact (games on this device).
 *
 * The rolls of a series are fixed BEFORE the first one is thrown: a secret seed
 * is drawn and only its SHA-256 is shown (the «fingerprint»). Roll i is then
 * SHA-256(seed ‖ i) turned into two dice by rejection sampling — the same rule
 * the online game uses. When the person asks to check, the seed is revealed,
 * every roll of the series is recomputed from it and compared, and a new seed
 * starts the next series. Neither the computer nor anybody else could steer a
 * die without breaking the fingerprint shown in advance — and anyone can redo
 * the computation with any SHA-256 tool.
 */
async function newFairSeries() {
    const seed = F.randomHex(32);
    const commit = await F.sha256Hex(seed);
    app.fair = Object.assign(app.fair || { verified: [] }, { seed, commit, log: [] });
    if (!app.fair.verified) app.fair.verified = [];
}
function counterHex(i) { return (i >>> 0).toString(16).padStart(8, '0'); }
async function nextDice() {
    try {
        if (!app.fair || !app.fair.seed) await newFairSeries();
        const i = app.fair.log.length;
        const dice = await F.diceOf(app.fair.seed, counterHex(i));
        app.fair.log.push(dice);
        return dice;
    } catch (err) {
        // no SubtleCrypto (an old browser, an insecure context): plain crypto dice
        diag('fair_dice_unavailable', { msg: String(err && err.message || err) });
        return [R.cryptoDie(), R.cryptoDie()];
    }
}
async function verifySeries(f) {
    const commit = await F.sha256Hex(f.seed);
    if (commit !== f.commit) return { ok: false, n: f.log.length, bad: -1 };
    for (let i = 0; i < f.log.length; i++) {
        const d = await F.diceOf(f.seed, counterHex(i));
        if (d[0] !== f.log[i][0] || d[1] !== f.log[i][1]) return { ok: false, n: f.log.length, bad: i };
    }
    return { ok: true, n: f.log.length };
}

/** The throw on screen: faces tumble for a moment, then land. */
function tumble(owner) {
    if (!settings.animate || reduceMotion()) return Promise.resolve();
    return new Promise((resolve) => {
        const t0 = performance.now();
        const iv = setInterval(() => {
            app.rolling = { owner, dice: [1 + ((Math.random() * 6) | 0), 1 + ((Math.random() * 6) | 0)] };
            drawBoard();
            if (performance.now() - t0 > 420) { clearInterval(iv); app.rolling = null; resolve(); }
        }, 70);
    });
}

/* ─────────────────────────────── the game loop ──────────────────────────── */

let loopTimer = null;
function schedule(fn, ms) {
    if (loopTimer) clearTimeout(loopTimer);
    loopTimer = setTimeout(() => { loopTimer = null; fn(); }, ms || 0);
}

/** Decide what happens next. Safe to call any time. */
function step() {
    const gm = g();
    if (!gm || app.busy) { render(); return; }
    switch (gm.phase) {
        case 'opening':
            if (app.mode === 'online') {
                if (app.netRole === 'host' && app.net && app.net.connected) schedule(openingOnline, 500);
            } else schedule(openingLocal, 450);
            break;
        case 'roll':
            if (isHuman(gm.turn)) {
                if (settings.autoRoll && !G.canDouble(app.m, gm.turn)) schedule(humanRoll, 350);
            } else if (app.mode === 'bot') { leaveReplay(); schedule(botRollPhase, 450); }
            break;
        case 'double':
            if (app.mode === 'bot' && gm.cube.offeredBy === app.me) schedule(botAnswerDouble, 600);
            else if (isHuman(1 - gm.cube.offeredBy)) app.overlay = { kind: 'cube' };
            break;
        case 'move':
            if (isHuman(gm.turn)) { if (!app.turn) beginHumanTurn(); }
            else if (app.mode === 'bot') { leaveReplay(); schedule(botMove, 250); }
            break;
        case 'over':
            if (!app.overlay || app.overlay.kind !== 'result') showResult();
            break;
    }
    render();
}

async function openingLocal() {
    const gm = g();
    if (!gm || gm.phase !== 'opening' || app.busy) return;
    app.busy = true;
    Snd.play('dice');
    const [[dW, dB]] = await Promise.all([nextDice(), tumble(-1)]);
    if (!sameGame(gm, 'opening')) { letGo(); return; }
    app.busy = false;
    applyOpening(dW, dB);
}

function applyOpening(dW, dB) {
    const started = G.openingRoll(app.m, dW, dB);
    const who = (p) => nameOf(p);
    if (!started) {
        setMsg(L('msg.opening_tie', { d: dW }), 'info');
        persist();
        step();
        return;
    }
    const p = g().turn;
    setMsg(L('msg.opening', { w: who(0), dw: dW, b: who(1), db: dB, first: who(p) }), 'info');
    stage('game_start', { variant: app.m.cfg.variant, mode: app.mode });
    persist();
    step();
}

async function humanRoll() {
    const gm = g();
    if (!gm || gm.phase !== 'roll' || !isHuman(gm.turn) || app.busy) return;
    leaveReplay();
    app.overlay = null;
    let dice;
    app.busy = true;
    Snd.play('dice');
    try {
        if (app.mode === 'online') {
            const k = rollKey();
            [dice] = await Promise.all([app.net.roll(k), tumble(gm.turn)]);
        } else {
            [dice] = await Promise.all([nextDice(), tumble(gm.turn)]);
        }
    } catch (err) {
        if (!sameGame(gm, 'roll')) { letGo(); return; }   // the link closed because the person left
        app.busy = false;
        setMsg(L('online.wait_link'), 'warn');
        render();
        return;
    }
    if (!sameGame(gm, 'roll')) { letGo(); return; }
    app.busy = false;
    G.roll(app.m, dice[0], dice[1]);
    app.hint = null;
    persist();
    step();
}

function rollKey() { const gm = g(); return 'r:' + app.m.gameNo + ':' + gm.history.length; }
function openKey() { const gm = g(); return 'o:' + app.m.gameNo + ':' + gm.opening.length; }

function beginHumanTurn() {
    const gm = g();
    app.turn = G.turnFor(app.m);
    app.sel = null;
    app.hint = null;
    if (app.turn.maxUsed === 0) {
        setMsg(L('msg.no_moves', { d: gm.dice.join('-') }), 'warn');
        render();
        schedule(() => commitHuman(true), 1500);
        return;
    }
    if (settings.autoForced) {
        const pl = G.plays(app.m);
        if (pl && pl.plays.length === 1) {
            setMsg(L('msg.forced'), 'info');
            render();
            // played for the person after a beat — unless he has started on it himself in
            // that beat. Until 20.10.2026 the timer played over his tap: the checker he had
            // picked lost its selection and his next tap, on a board that had changed under
            // it, was refused («that point is closed by your opponent»)
            const turn = app.turn;
            schedule(() => {
                if (app.turn !== turn || app.busy) return;
                if (turn.played.length || app.sel != null) { feature('bg_forced_own', {}); return; }
                playSteps(pl.plays[0].moves, true);
            }, 500);
            return;
        }
    }
    setMsg(app.mode === 'local' ? L('msg.turn_of', { who: nameOf(gm.turn), d: gm.dice.join('-') }) : L('msg.your_turn', { d: gm.dice.join('-') }), '');
    render();
}

/** Play steps with the animation, from the current state of the live turn. */
async function playSteps(steps, andCommit) {
    if (!app.turn) return;
    app.busy = true;
    const turn = app.turn;
    const p = turn.p;
    for (const s of steps) {
        if (settings.animate && !reduceMotion()) {
            board.set({ pos: turn.position(), sources: new Set(), dests: new Map(), selected: null, showLast: false });
            await board.animateStep(p, s.from, s.to, 170);
            if (app.turn !== turn) { letGo(); return; }
        }
        if (!turn.play({ from: s.from, d: s.d })) break;
        Snd.play(s.hit ? 'hit' : s.to === R.OFF ? 'off' : 'move');
        drawBoard();
    }
    app.busy = false;
    app.sel = null;
    if (app.turn && app.turn.done && (andCommit || settings.autoConfirm)) { commitHuman(); return; }
    if (app.turn && app.turn.done) setMsg(L('msg.confirm_hint'), '');
    render();
}

function commitHuman(pass) {
    const gm = g();
    if (!gm || gm.phase !== 'move' || !app.turn) return;
    if (!pass && !app.turn.done) return;
    const played = app.turn.played.map((u) => ({ from: u.from, to: u.to, hit: u.hit, d: u.d }));
    const p = gm.turn;
    const k = rollKey();
    if (!G.commitTurn(app.m, played)) {
        diag('commit_refused', { n: played.length });
        return;
    }
    app.turn = null;
    app.last = played; app.lastBy = p;
    app.hint = null;
    if (app.mode === 'online') netSend({ t: 'mv', k, moves: played.map((u) => [u.from, u.d]) });
    if (!app.firstMoveSent && isHuman(p)) { app.firstMoveSent = true; stage('first_move', { variant: app.m.cfg.variant, mode: app.mode }); }
    setMsg('', '');
    persist();
    step();
}

/* ─────────────────────────── the computer's turn ────────────────────────── */

function botBudget() { return (navigator.hardwareConcurrency || 4) <= 4 ? 450 : 700; }

async function botRollPhase() {
    const gm = g();
    if (!gm || gm.phase !== 'roll' || app.mode !== 'bot' || isHuman(gm.turn) || app.busy) return;
    const p = gm.turn;
    if (G.canDouble(app.m, p)) {
        app.busy = true;
        const r = await botAsk({ kind: 'double', pos: gm.pos, p });
        if (!sameGame(gm, 'roll')) { letGo(); return; }
        app.busy = false;
        if (r && r.yes && G.offerDouble(app.m, p)) {
            setMsg(L('msg.bot_doubles', { v: gm.cube.value * 2 }), 'warn');
            app.overlay = { kind: 'cube' };
            persist();
            render();
            return;
        }
    }
    app.busy = true;
    setMsg(L('msg.bot_rolls'), '');
    Snd.play('dice');
    const [d] = await Promise.all([nextDice(), tumble(p)]);
    if (!sameGame(gm, 'roll')) { letGo(); return; }
    app.busy = false;
    G.roll(app.m, d[0], d[1]);
    persist();
    step();
}

async function botMove() {
    const gm = g();
    if (!gm || gm.phase !== 'move' || app.mode !== 'bot' || isHuman(gm.turn) || app.busy) return;
    const p = gm.turn;
    app.busy = true;
    setMsg(L('msg.bot_thinks'), '');
    render();
    const t0 = performance.now();
    let moves;
    try {
        const r = await botAsk({
            kind: 'play', pos: gm.pos, p, d1: gm.dice[0], d2: gm.dice[1], level: app.level,
            firstTurn: G.firstTurnOf(gm, p), oppFirstTurn: G.firstTurnOf(gm, 1 - p), budgetMs: botBudget(),
        });
        moves = r.moves;
        app.lastEngine = r.engine || 'heuristic';
    } catch (err) {
        if (!sameGame(gm, 'move')) { letGo(); return; }
        diag('bot_failed', { msg: String(err && err.message || err) });
        moves = G.plays(app.m).plays[0].moves;
    }
    // a computer that answers instantly feels like it did not think
    const wait = 350 - (performance.now() - t0);
    if (wait > 0) await new Promise((r) => setTimeout(r, wait));
    if (!sameGame(gm, 'move')) { letGo(); return; }
    // animate on a temporary turn
    const t = G.turnFor(app.m);
    for (const s of moves) {
        board.set({ pos: t.position(), mover: p, sources: new Set(), dests: new Map(), selected: null, usedDice: t.played.map((u) => u.d), showLast: false, hint: null });
        if (settings.animate && !reduceMotion()) {
            await board.animateStep(p, s.from, s.to, 260);
            if (!sameGame(gm, 'move')) { letGo(); return; }
        }
        t.play({ from: s.from, d: s.d });
        Snd.play(s.hit ? 'hit' : s.to === R.OFF ? 'off' : 'move');
    }
    if (!moves.length) setMsg(L('msg.bot_no_moves', { d: gm.dice.join('-') }), 'info');
    app.busy = false;
    if (!G.commitTurn(app.m, moves)) {
        diag('bot_illegal', { n: moves.length });
        G.commitTurn(app.m, G.plays(app.m).plays[0].moves);
    }
    app.last = moves; app.lastBy = p;
    if (moves.length) setMsg(L('msg.bot_played', { d: gm.history[gm.history.length - 1].dice.join('-'), n: R.notate(moves) }), '');
    persist();
    step();
}

async function botAnswerDouble() {
    const gm = g();
    if (!gm || gm.phase !== 'double') return;
    const taker = 1 - gm.cube.offeredBy;
    app.busy = true;
    const r = await botAsk({ kind: 'take', pos: gm.pos, p: taker });
    if (!sameGame(gm, 'double')) { letGo(); return; }
    app.busy = false;
    if (r && r.yes) {
        G.takeDouble(app.m);
        setMsg(L('msg.bot_takes', { v: gm.cube.value }), 'info');
    } else {
        G.dropDouble(app.m);
        setMsg(L('msg.bot_drops'), 'info');
    }
    persist();
    step();
}

/* ─────────────────────────── cube, resign, result ───────────────────────── */

function humanDouble() {
    const gm = g();
    if (!gm || !G.canDouble(app.m, gm.turn) || !isHuman(gm.turn)) return;
    // a cube offer cannot be taken back — one more word before it goes
    app.overlay = { kind: 'confirm', text: L('msg.double_q', { v: gm.cube.value * 2 }), yes: () => {
        app.overlay = null;
        G.offerDouble(app.m, gm.turn);
        if (app.mode === 'online') netSend({ t: 'dbl' });
        setMsg(app.mode === 'local' ? '' : L('msg.you_doubled', { v: gm.cube.value * 2 }), 'info');
        persist();
        step();
    } };
    render();
}

function answerDouble(take) {
    const gm = g();
    if (!gm || gm.phase !== 'double') return;
    app.overlay = null;
    if (take) G.takeDouble(app.m); else G.dropDouble(app.m);
    if (app.mode === 'online') netSend({ t: take ? 'take' : 'drop' });
    persist();
    step();
}

function resign() {
    const gm = g();
    if (!gm || gm.phase === 'over') return;
    const p = app.mode === 'local' ? gm.turn : app.me;
    const res = R.resultOf(gm.pos, 1 - p, { koksLong: app.m.cfg.koksLong });
    app.overlay = { kind: 'confirm', text: L('msg.resign_q', { n: res.points * gm.cube.value }), yes: () => {
        G.resign(app.m, p);
        if (app.mode === 'online') netSend({ t: 'resign' });
        app.overlay = null;
        persist();
        step();
    } };
    render();
}

function showResult() {
    const gm = g();
    if (!gm || !gm.result) return;
    const r = gm.result;
    const mine = app.mode === 'local' ? null : r.winner === app.me;
    Snd.play(mine === false ? 'lose' : 'win');
    app.overlay = { kind: 'result' };
    app.turn = null;
    if (canAutoReview()) setTimeout(() => runReview(true), 600);
    try {
        window.__aeToolComplete && window.__aeToolComplete({
            variant: app.m.cfg.variant, mode: app.mode, level: app.mode === 'bot' ? app.level : undefined,
            result: mine == null ? 'hotseat' : mine ? 'win' : 'loss', points: r.points, kind: r.kind,
            turns: gm.history.length, match_to: app.m.cfg.matchTo, match_over: app.m.over ? 1 : 0,
        });
    } catch (_) {}
    persist();
    render();
}

function nextGame(fromRemote) {
    if (!app.m) return;
    if (app.reviewing) { stopReview(); app.reviewing = false; }
    if (app.m.over) { newMatchSameSettings(fromRemote); return; }
    if (!G.nextGame(app.m)) return;
    if (app.mode === 'online' && !fromRemote) netSend({ t: 'next' });
    app.overlay = null; app.turn = null; app.last = null; app.hint = null; app.replay = null; app.review = null; app.meter = null;
    persist();
    step();
}

function newMatchSameSettings(fromRemote) {
    const cfg = Object.assign({}, app.m.cfg);
    if (app.mode === 'online') {
        if (app.netRole !== 'host') {
            if (!fromRemote) { netSend({ t: 'rematch_req' }); setMsg(L('online.rematch_asked'), 'info'); render(); }
            return;
        }
        app.m = G.newMatch(cfg);
        netSend({ t: 'sync', m: app.m });
    } else {
        app.m = G.newMatch(cfg);
    }
    app.overlay = null; app.turn = null; app.last = null; app.hint = null; app.replay = null; app.review = null; app.meter = null;
    persist();
    step();
}

/* ─────────────────────────────── online ─────────────────────────────────── */

function netSend(msg) { return app.net ? app.net.send(msg) : false; }

function netStatus(k, d) {
    app.netState = k;
    const box = $('bgNetStatus');
    const map = {
        waiting: L('online.waiting'), connected: L('online.connected'), joining: L('online.joining'),
        lost: L('online.lost'), quiet: L('online.quiet'), full: L('online.full'),
        not_found: L('online.not_found'), error: L('online.error'), unavailable: L('online.error'),
        rejoining: L('online.rejoining'), reclaiming: L('online.reclaiming'), cheat: L('online.cheat'),
    };
    if (k === 'latency') { const el = $('bgNetLat'); if (el) el.textContent = d + ' ms'; return; }
    if (k === 'cheat') diag('online_bad_reveal', {});
    if (k === 'waiting' && d) showLink(d);
    if (box && map[k]) { box.textContent = map[k]; box.dataset.kind = /lost|full|not_found|error|cheat|quiet/.test(k) ? 'bad' : k === 'connected' ? 'good' : ''; }
    render();
}

function shareUrl(code) { return location.origin + '/' + (CFG.locale || 'en') + '/' + SLUG + '?room=' + code; }

function showLink(code) {
    const url = shareUrl(code);
    const inp = $('bgLink');
    if (inp) inp.value = url;
    $('bgLinkBox').hidden = false;
    const c = $('bgCode'); if (c) c.textContent = code.toUpperCase();
    const qr = $('bgQr');
    if (qr) {
        qr.innerHTML = '';
        try {
            if (window.__QR && window.__QR.render) window.__QR.render(qr, url, 132);
            else if (window.QRCode) new window.QRCode(qr, { text: url, width: 132, height: 132 });
        } catch (_) {}
    }
    // the link is what the host needs next: show the panel that holds it
    selectTab('game');
}

function netCallbacks() {
    return {
        onStatus: netStatus,
        onConnected: (role) => {
            if (role === 'host') {
                // the guest adopts our match on every connection
                netSend({ t: 'sync', m: app.m });
                stage('online_connected', { role });
                step();
            }
        },
        onPeerHello: () => { if (app.m) netSend({ t: 'sync', m: app.m }); step(); },
        onSync: (m) => {
            const mm = G.deserialize(m);
            if (!mm) { diag('online_bad_sync', {}); return; }
            const before = app.m ? G.serialize(app.m) : '';
            app.m = mm;
            if (G.serialize(mm) !== before) { app.turn = null; app.sel = null; app.overlay = null; app.replay = null; }
            showGameUi();
            persist();
            step();
        },
        onRemoteRoll: (k, dice) => {
            const gm = g();
            if (!gm) return;
            if (k.startsWith('o:')) {
                if (gm.phase === 'opening' && k === openKey()) applyOpening(dice[0], dice[1]);
                return;
            }
            if (gm.phase === 'roll' && !isHuman(gm.turn) && k === rollKey()) {
                Snd.play('dice');
                G.roll(app.m, dice[0], dice[1]);
                setMsg(L('msg.opp_rolled', { d: dice.join('-') }), '');
                persist();
                step();
            }
        },
        onAction: (msg) => onRemoteAction(msg),
    };
}

async function openingOnline() {
    const gm = g();
    if (!gm || gm.phase !== 'opening' || app.netRole !== 'host' || app.busy || !(app.net && app.net.connected)) return;
    app.busy = true;
    Snd.play('dice');
    let dice;
    try { [dice] = await Promise.all([app.net.roll(openKey()), tumble(-1)]); }
    catch (_) {
        if (!sameGame(gm, 'opening')) { letGo(); return; }
        app.busy = false; return;
    }
    // the same continuation as openingLocal (Sentry TIMBRICA-FRONTEND-PA):
    // the guest's nonce can arrive after «Leave» — app.m is null by then
    if (!sameGame(gm, 'opening')) { letGo(); return; }
    app.busy = false;
    applyOpening(dice[0], dice[1]);
}

async function onRemoteAction(msg) {
    const gm = g();
    if (!gm) return;
    const remote = 1 - app.me;
    switch (msg.t) {
        case 'mv': {
            if (gm.phase !== 'move' || gm.turn !== remote || msg.k !== rollKey() || !Array.isArray(msg.moves)) return;
            const moves = [];
            const t = G.turnFor(app.m);
            for (const [from, d] of msg.moves) {
                const ok = t.played.length < 4 && t.play({ from: +from, d: +d });
                if (!ok) { refuseRemote('illegal_move'); return; }
                const u = t.played[t.played.length - 1];
                moves.push({ from: u.from, to: u.to, hit: u.hit, d: u.d });
            }
            if (!t.done) { refuseRemote('incomplete_move'); return; }
            leaveReplay();
            app.busy = true;
            const anim = G.turnFor(app.m);
            for (const s of moves) {
                board.set({ pos: anim.position(), mover: remote, sources: new Set(), dests: new Map(), selected: null, showLast: false });
                if (settings.animate && !reduceMotion()) {
                    await board.animateStep(remote, s.from, s.to, 220);
                    // «Leave» (app.m → null) or the host's sync (a match that
                    // already holds this move) during the animation: committing
                    // now would throw, or play the move a second time
                    if (!sameGame(gm, 'move')) { letGo(); return; }
                }
                anim.play({ from: s.from, d: s.d });
                Snd.play(s.hit ? 'hit' : s.to === R.OFF ? 'off' : 'move');
            }
            app.busy = false;
            G.commitTurn(app.m, moves);
            app.last = moves; app.lastBy = remote;
            setMsg(moves.length ? L('msg.opp_played', { n: R.notate(moves) }) : L('msg.opp_no_moves'), '');
            persist();
            step();
            return;
        }
        case 'dbl':
            if (G.offerDouble(app.m, remote)) { setMsg(L('msg.opp_doubles', { v: gm.cube.value * 2 }), 'warn'); persist(); step(); }
            return;
        case 'take':
            if (gm.phase === 'double' && gm.cube.offeredBy === app.me) { G.takeDouble(app.m); setMsg(L('msg.opp_takes', { v: gm.cube.value }), 'info'); persist(); step(); }
            return;
        case 'drop':
            if (gm.phase === 'double' && gm.cube.offeredBy === app.me) { G.dropDouble(app.m); persist(); step(); }
            return;
        case 'resign':
            if (gm.phase !== 'over') { G.resign(app.m, remote); setMsg(L('msg.opp_resigned'), 'info'); persist(); step(); }
            return;
        case 'next':
            if (gm.phase === 'over' && !app.m.over) nextGame(true);
            return;
        case 'rematch_req':
            if (app.netRole === 'host' && app.m.over) newMatchSameSettings(true);
            return;
        case 'syncreq':
            if (app.netRole === 'host') netSend({ t: 'sync', m: app.m });
            return;
    }
}

function refuseRemote(why) {
    diag('online_' + why, {});
    setMsg(L('online.desync'), 'warn');
    if (app.netRole === 'host') netSend({ t: 'sync', m: app.m });
    else netSend({ t: 'syncreq' });
    render();
}

function netUnavailable(err) {
    diag('online_module_failed', { msg: String(err && err.message || err) });
    netStatus('error');
}

function startOnlineHost() {
    if (!window.__RoomConnect) { toast(L('online.error'), 'error'); return; }
    stopOnline(true);
    app.mode = 'online';
    app.me = 0;
    app.m = G.newMatch(matchCfg());
    app.netRole = 'host';
    stage('online_host', {});
    showGameUi();
    render();
    loadOnline().then((O) => {
        if (app.mode !== 'online' || app.netRole !== 'host' || app.net) return;
        app.net = O.create(netCallbacks());
        app.netCode = app.net.host();
        try { history.replaceState(null, '', location.pathname + '?host=' + app.netCode); } catch (_) {}
        persist();
        render();
    }, netUnavailable);
}

function joinOnline(code, resumeMatch) {
    if (!window.__RoomConnect) { toast(L('online.error'), 'error'); return; }
    stopOnline(false);
    app.mode = 'online';
    app.me = 1;
    app.netRole = 'guest';
    app.netCode = code;
    app.m = resumeMatch || G.newMatch(matchCfg());
    stage('online_join', {});
    showGameUi();
    persist();
    render();
    loadOnline().then((O) => {
        if (app.mode !== 'online' || app.netCode !== code || app.net) return;
        app.net = O.create(netCallbacks());
        app.net.join(code);
    }, netUnavailable);
}

function stopOnline(clearStamp) {
    if (app.net) { try { app.net.destroy(clearStamp); } catch (_) {} }
    app.net = null; app.netRole = null; app.netCode = null;
    const lb = $('bgLinkBox'); if (lb) lb.hidden = true;
}

/* ─────────────────────────────── starting ───────────────────────────────── */

function matchCfg() {
    return { variant: setup.variant, matchTo: +setup.matchTo, cube: !!setup.cube, crawford: !!setup.crawford, koksLong: !!setup.koksLong };
}

function startGame() {
    if (setup.mode === 'online') { startOnlineHost(); return; }
    stopOnline(true);
    try { if (location.search) history.replaceState(null, '', location.pathname); } catch (_) {}
    app.mode = setup.mode;
    app.level = setup.level;
    app.me = 0;
    app.m = G.newMatch(matchCfg());
    app.turn = null; app.sel = null; app.last = null; app.hint = null; app.overlay = null; app.firstMoveSent = false; app.replay = null; app.review = null; app.meter = null;
    setMsg('', '');
    showGameUi();
    ensureNets();
    newFairSeries().then(() => { persist(); step(); }, () => { persist(); step(); });
}

function showGameUi() {
    $('bgSetup').hidden = true;
    $('bgMatch').hidden = false;
    $('bgApp').dataset.state = 'game';
    wake(true);
    fitSoon();
}

function showSetup() {
    $('bgSetup').hidden = false;
    $('bgMatch').hidden = true;
    $('bgApp').dataset.state = 'setup';
    selectTab('game');
    renderSetup();
    fitSoon();
}

/* ─────────────────────────────── rendering ──────────────────────────────── */

function setMsg(text, kind) { app.msg = text || ''; app.msgKind = kind || ''; }

/** Positions after each entry of the history, for the move list replay. */
function replayPositions(gm) {
    const out = [];
    let pos = R.initialPosition(gm.v);
    for (const h of gm.history) {
        if (h.moves) pos = R.applyMoves(pos, h.p, h.moves);
        out.push(pos);
    }
    return out;
}

function drawBoard() {
    if (!board) return;
    const gm = g();
    const base = { numbers: settings.numbers, mirror: settings.mirror };
    if (!gm) {
        board.set(Object.assign(base, { pos: R.initialPosition(setup.variant), viewer: 0, mover: 0, sources: new Set(), dests: new Map(), selected: null, dice: null, cube: null, last: null, hint: null, played: null, busy: true, rolling: false, meter: null }));
        return;
    }
    if (app.replay != null && gm.history[app.replay]) {
        const h = gm.history[app.replay];
        const all = replayPositions(gm);
        const before = app.replay > 0 ? all[app.replay - 1] : R.initialPosition(gm.v);
        const t = revTurn(app.replay);
        const showBest = !!t && !t.bestSame && t.err >= 0.005 && t.cls !== 'forced' && turnOpen(t);
        board.set(Object.assign(base, {
            pos: before, viewer: viewer(), mover: h.p, sources: new Set(), dests: new Map(), selected: null,
            dice: h.dice || null, diceOwner: h.p, usedDice: [],
            cube: null, last: null, showLast: false, played: h.moves || null, playedBy: h.p,
            hint: showBest ? t.best : null, hintBy: h.p, busy: true, rolling: false, meter: null,
        }));
        return;
    }
    const mover = gm.turn >= 0 ? gm.turn : 0;
    const pos = app.turn ? app.turn.position() : gm.pos;
    const canAct = !app.busy && gm.phase === 'move' && app.turn && isHuman(gm.turn) && !ovOpen();
    const sources = new Set();
    const dests = new Map();
    if (canAct) {
        for (const s of app.turn.legalSteps()) sources.add(s.from);
        if (app.sel != null) for (const d of app.turn.destinations(app.sel)) dests.set(d.to, d);
    }
    let dice = gm.dice, owner = mover;
    if (app.rolling) { dice = app.rolling.dice; owner = app.rolling.owner < 0 ? mover : app.rolling.owner; }
    else if (gm.phase === 'opening' && gm.opening.length) { dice = gm.opening[gm.opening.length - 1]; }
    board.set(Object.assign(base, {
        pos, viewer: viewer(), mover, sources, dests, selected: canAct ? app.sel : null,
        dice, diceOwner: owner, usedDice: app.turn ? app.turn.played.map((u) => u.d) : [],
        cube: G.cubeAvailable(app.m) ? gm.cube : null,
        last: app.last, lastBy: app.lastBy,
        showLast: settings.lastArrows && !(app.turn && app.turn.played.length) && app.sel == null,
        hint: app.hint, hintBy: null, played: null, busy: app.busy || ovOpen(), rolling: !!app.rolling,
        meter: settings.meter && app.meter != null ? app.meter : null,
    }));
}

function chip(el, value, label) {
    el.textContent = '';
    if (value == null || value === '') return;
    const b = document.createElement('b'); b.textContent = String(value);
    const s = document.createElement('small'); s.textContent = label;
    el.appendChild(b); el.appendChild(s);
}

function renderPlayers() {
    const gm = g();
    const vw = viewer();
    let pos = gm ? (app.turn ? app.turn.position() : gm.pos) : R.initialPosition(setup.variant);
    if (gm && app.replay != null && gm.history[app.replay]) pos = app.replay > 0 ? replayPositions(gm)[app.replay - 1] : R.initialPosition(gm.v);
    const mode = gm ? app.mode : setup.mode;
    for (const [slot, p] of [['bgPTop', 1 - vw], ['bgPBot', vw]]) {
        const box = $(slot);
        if (!box) continue;
        box.querySelector('.bg-disc').className = 'bg-disc ' + (p === 0 ? 'bg-disc-w' : 'bg-disc-b');
        let name, meta = '';
        if (gm) { name = nameOf(p); }
        else if (mode === 'bot') { name = p === 0 ? L('ui.you') : L('setup.mode_bot'); }
        else if (mode === 'online') { name = p === 0 ? L('ui.you') : L('ui.opponent'); }
        else name = colorName(p);
        if ((gm ? app.mode : mode) === 'bot' && p !== 0) meta = L('setup.level_' + (gm ? app.level : setup.level));
        else if ((gm ? app.mode : mode) !== 'local') meta = colorName(p);
        box.querySelector('.bg-p-name').textContent = name;
        box.querySelector('.bg-p-meta').textContent = meta;
        chip(box.querySelector('.bg-p-pips'), R.pipCount(pos, p), L('ui.pips_short'));
        chip(box.querySelector('.bg-p-off'), pos.off[p] ? pos.off[p] + '/15' : '', L('ui.off_short'));
        const sc = box.querySelector('.bg-p-score');
        if (gm && app.m.cfg.matchTo !== 1) chip(sc, app.m.cfg.matchTo > 0 ? app.m.score[p] + '/' + app.m.cfg.matchTo : app.m.score[p], L('ui.score_short'));
        else chip(sc, '', '');
        const onTurn = app.replay != null && gm && gm.history[app.replay] ? gm.history[app.replay].p === p : !!gm && gm.turn === p && gm.phase !== 'over' && gm.phase !== 'opening';
        box.classList.toggle('bg-on-turn', onTurn);
    }
}

function renderActions() {
    const gm = g();
    const on = (id, show, enabled) => { const b = $(id); if (!b) return; b.hidden = !show; b.disabled = show && enabled === false; };
    const human = gm && isHuman(gm.turn) && !app.busy && !ovOpen() && app.replay == null;
    on('bgRoll', !!gm && gm.phase === 'roll' && human);
    on('bgDouble', !!gm && gm.phase === 'roll' && human && G.canDouble(app.m, gm.turn));
    const moving = !!gm && gm.phase === 'move' && human && !!app.turn;
    on('bgUndo', moving, !!app.turn && app.turn.played.length > 0);
    on('bgDone', moving && !settings.autoConfirm, !!app.turn && app.turn.done);
    on('bgHint', moving && app.mode !== 'online' && app.turn.maxUsed > 0);
    // «Resign» keeps its place while it cannot be pressed (the opening roll, the end):
    // hidden, it let «New game» next to it jump from half the row to all of it
    const canResign = !!gm && gm.phase !== 'over' && gm.phase !== 'opening';
    on('bgResign', !!gm, canResign);
    $('bgResign').style.visibility = canResign ? '' : 'hidden';
    const nx = $('bgNext');
    if (nx) nx.hidden = !(gm && gm.phase === 'over' && !ovOpen()) || app.replay != null;
    const rp = app.replay != null;
    $('bgPrev').hidden = !rp; $('bgFwd').hidden = !rp; $('bgBack').hidden = !rp;
    if (rp) {
        $('bgPrev').disabled = replayStep(-1) == null;
        $('bgFwd').disabled = replayStep(1) == null;
    }
}

/** The next / previous move of the history from the one shown (null at the ends). */
function replayStep(dir) {
    const gm = g();
    if (!gm || app.replay == null) return null;
    for (let i = app.replay + dir; i >= 0 && i < gm.history.length; i += dir) if (gm.history[i].moves) return i;
    return null;
}
function goReplay(dir) {
    const i = replayStep(dir);
    if (i == null) return;
    app.replay = i;
    render();
}

function statusText() {
    const gm = g();
    if (!gm) return L('msg.pick_setup');
    if (app.msg) return app.msg;
    if (app.mode === 'online' && !app.net) return '';
    if (app.mode === 'online' && app.netState && app.netState !== 'connected' && app.netState !== 'latency') {
        if (gm.phase === 'opening' || !(app.net && app.net.connected)) return app.netRole === 'host' ? L('online.waiting') : L('online.joining');
    }
    switch (gm.phase) {
        case 'opening': return L('msg.opening_roll');
        case 'roll':
            if (isHuman(gm.turn)) return app.mode === 'local' ? L('msg.roll_of', { who: nameOf(gm.turn) }) : L('msg.roll');
            return app.mode === 'online' ? L('msg.opp_turn') : L('msg.bot_rolls');
        case 'double': return L('msg.cube_wait');
        case 'move':
            if (isHuman(gm.turn)) return app.turn && app.turn.done ? L('msg.confirm_hint') : L('msg.your_turn', { d: gm.dice.join('-') });
            return app.mode === 'online' ? L('msg.opp_moves') : L('msg.bot_thinks');
        case 'over': {
            const r = gm.result;
            if (!r) return '';
            if (app.reviewing && !app.reviewQuiet) return L('rev.running');
            const who = app.mode === 'local' ? L('res.winner_is', { who: nameOf(r.winner) }) : (r.winner === app.me ? L('res.you_win') : L('res.you_lose'));
            return who + ' ' + L('res.kind_' + (r.kind.replace('resign-', '') === 'drop' ? 'drop' : r.kind.replace('resign-', '')), { n: r.points });
        }
    }
    return '';
}

function renderStatus() {
    const s = $('bgStatus');
    if (app.replay != null && g()) { renderReplayStatus(s); fitStatus(s); return; }
    const text = statusText();
    if (s.textContent !== text || s.childElementCount) { s.textContent = text; fitStatus(s); }
    s.dataset.kind = app.msgKind || '';
}

/* the status box has a fixed height (two lines): a longer text is set smaller, down
   to 12 px, instead of making the box — and everything under it — move */
function fitStatus(s) {
    s.style.fontSize = '';
    if (s.hidden || !s.offsetParent) return;
    let fs = 14;
    while (s.scrollHeight > s.clientHeight + 1 && fs > 12) { fs -= 0.5; s.style.fontSize = fs + 'px'; }
}

/** Under the board while a past move is shown: which move, and what the review says of it. */
function renderReplayStatus(s) {
    const gm = g();
    const h = gm.history[app.replay];
    let k = 0;
    for (let i = 0; i <= app.replay; i++) if (gm.history[i] && gm.history[i].moves) k++;
    s.textContent = '';
    const head = document.createElement('span');
    head.textContent = L('ui.replay_of', { n: k, d: h.dice.join('-'), m: h.note || L('ui.log_pass') }) + ' ';
    const box = document.createElement('span');
    box.appendChild(head);
    const t = revTurn(app.replay);
    let kind = 'info';
    if (t) {
        const v = document.createElement('span');
        if (t.cls === 'forced') v.textContent = L('rev.forced');
        else if (t.bestSame || t.err < 0.005) { v.textContent = L('rev.was_best'); kind = 'good'; }
        else if (t.cls === 'ok') {
            v.textContent = L('rev.near_best', { e: t.err.toFixed(3) }) + (turnOpen(t) ? ' ' + L('rev.better', { m: R.notate(t.best) }) : '');
            kind = 'good';
        } else {
            // the cost in win chances where they say it (a beginner reads «48 % instead of
            // 61 %», not «−0.078»); the equity only where they do not (gammons, no net)
            const w = winWords(t);
            const verdict = L('rev.cls_' + t.cls) + (w ? ': ' + w + '.' : ' (−' + t.err.toFixed(3) + ').');
            kind = 'warn';
            if (turnOpen(t)) v.textContent = verdict + ' ' + L('rev.better', { m: R.notate(t.best) });
            else {
                v.textContent = verdict + ' ' + L('rev.best_locked') + ' ';
                v.appendChild(proLink());
            }
        }
        box.appendChild(v);
    } else {
        const v = document.createElement('span');
        v.textContent = L('ui.replay_hint');
        box.appendChild(v);
    }
    s.appendChild(box);
    s.dataset.kind = kind;
}

function renderOverlay() {
    const box = $('bgOverlay');
    const ov = app.overlay;
    const gm = g();
    if (!ov || !gm || app.replay != null) { box.hidden = true; box.innerHTML = ''; return; }
    box.hidden = false;
    box.innerHTML = '';
    const card = document.createElement('div');
    card.className = 'bg-ov-card';
    card.setAttribute('role', 'dialog');
    card.setAttribute('aria-modal', 'false');
    const h = document.createElement('div');
    h.className = 'bg-ov-h';
    const p = document.createElement('p');
    p.className = 'bg-ov-p';
    const row = document.createElement('div');
    row.className = 'bg-ov-row';
    const btn = (label, cls, fn, id) => {
        const b = document.createElement('button');
        b.type = 'button'; b.className = 'bg-btn ' + (cls || ''); b.textContent = label;
        if (id) b.id = id;
        b.addEventListener('click', fn);
        row.appendChild(b);
        return b;
    };
    let first = null;
    if (ov.kind === 'cube') {
        const offerer = gm.cube.offeredBy;
        h.textContent = L('cube.offer_h', { who: nameOf(offerer), v: gm.cube.value * 2 });
        p.textContent = L('cube.offer_p', { v: gm.cube.value * 2, drop: gm.cube.value });
        first = btn(L('cube.take'), 'bg-btn-pri', () => answerDouble(true), 'bgTake');
        btn(L('cube.drop'), '', () => answerDouble(false), 'bgDrop');
    } else if (ov.kind === 'confirm') {
        h.textContent = ov.text;
        first = btn(L('ui.yes'), 'bg-btn-pri', ov.yes, 'bgYes');
        btn(L('ui.cancel'), '', () => { app.overlay = null; render(); });
    } else if (ov.kind === 'result') {
        const r = gm.result;
        const kind = r.kind.replace('resign-', '');
        const who = nameOf(r.winner);
        h.textContent = app.mode === 'local' ? L('res.winner_is', { who }) : (r.winner === app.me ? L('res.you_win') : L('res.you_lose'));
        let txt = L('res.kind_' + (kind === 'drop' ? 'drop' : kind), { n: r.points });
        if (r.kind.startsWith('resign-')) txt = L('res.resigned') + ' ' + txt;
        if (r.cube > 1) txt += ' ' + L('res.cube', { v: r.cube });
        p.textContent = txt;
        const sc = document.createElement('p');
        sc.className = 'bg-ov-score';
        if (app.m.cfg.matchTo !== 1) sc.textContent = L('res.score', { a: nameOf(0), b: nameOf(1), x: app.m.score[0], y: app.m.score[1] });
        card.appendChild(h); card.appendChild(p); card.appendChild(sc);
        const rev = app.review && app.review.gameNo === app.m.gameNo ? app.review : null;
        if (rev) {
            const acc = document.createElement('p');
            acc.className = 'bg-ov-acc';
            const sides = app.mode === 'local' ? [0, 1] : [app.me];
            acc.textContent = sides.map((q) => {
                const sd = rev.sides[q];
                return (app.mode === 'local' ? nameOf(q) + ': ' : '') + L('rev.card', { a: sd.accuracy, e: sd.blunder + sd.mistake });
            }).join(' · ');
            card.appendChild(acc);
        } else if (app.reviewing) {
            const acc = document.createElement('p');
            acc.className = 'bg-ov-acc bg-ov-acc-wait';
            acc.textContent = L('rev.running');
            card.appendChild(acc);
        }
        if (app.m.over) {
            const mw = document.createElement('p');
            mw.className = 'bg-ov-match';
            mw.textContent = app.mode === 'local' ? L('res.match_winner', { who: nameOf(app.m.winner) }) : (app.m.winner === app.me ? L('res.match_you') : L('res.match_opp'));
            card.appendChild(mw);
            first = btn(L('res.new_match'), 'bg-btn-pri', () => nextGame(false), 'bgNextGame');
        } else {
            first = btn(app.m.cfg.matchTo === 1 ? L('res.again') : L('res.next_game'), 'bg-btn-pri', () => nextGame(false), 'bgNextGame');
        }
        btn(L('rev.run_short'), '', () => openReview('card'), 'bgReviewOv');
        btn(L('res.close'), 'bg-btn-ghost', () => { app.overlay = { kind: 'closed' }; render(); });
        card.appendChild(row);
        box.appendChild(card);
        focusFirst(first);
        return;
    } else if (ov.kind === 'closed') {
        box.hidden = true;
        return;
    }
    card.appendChild(h); card.appendChild(p); card.appendChild(row);
    box.appendChild(card);
    focusFirst(first);
}
let lastOverlayFocus = '';
function focusFirst(b) {
    // move the keyboard to a new card once, never scroll the page for it
    const key = app.overlay ? app.overlay.kind + ':' + (g() ? g().history.length : 0) : '';
    if (!b || key === lastOverlayFocus) return;
    lastOverlayFocus = key;
    try { b.focus({ preventScroll: true }); } catch (_) {}
}

function renderMoves() {
    const list = $('bgMoves');
    if (!list) return;
    const gm = g();
    list.textContent = '';
    if (!gm) return;
    const frag = document.createDocumentFragment();
    let n = 0;
    const add = (txt, p, cls, i, dice) => {
        const li = document.createElement('li');
        li.className = 'bg-mv ' + (cls || '');
        const d = document.createElement('span');
        d.className = 'bg-mv-dot ' + (p === 0 ? 'bg-disc-w' : 'bg-disc-b');
        li.appendChild(d);
        if (dice) { const s = document.createElement('span'); s.className = 'bg-mv-dice'; s.textContent = dice; li.appendChild(s); }
        const t = document.createElement('span');
        t.textContent = txt;
        li.appendChild(t);
        if (i != null) {
            li.dataset.i = String(i);
            li.tabIndex = 0;
            li.setAttribute('role', 'button');
            if (app.replay === i) li.classList.add('on');
        }
        frag.appendChild(li);
    };
    for (const o of gm.opening) {
        const li = document.createElement('li');
        li.className = 'bg-mv bg-mv-open';
        li.textContent = L('ui.opening_line', { w: o[0], b: o[1] });
        frag.appendChild(li);
    }
    gm.history.forEach((h, i) => {
        if (h.kind === 'double') { add(L('ui.log_double', { who: nameOf(h.p), v: h.value }), h.p, 'bg-mv-cube'); return; }
        if (h.kind === 'take') { add(L('ui.log_take', { who: nameOf(h.p), v: h.value }), h.p, 'bg-mv-cube'); return; }
        if (h.kind === 'drop') { add(L('ui.log_drop', { who: nameOf(h.p) }), h.p, 'bg-mv-cube'); return; }
        if (h.kind === 'resign') { add(L('ui.log_resign', { who: nameOf(h.p) }), h.p, 'bg-mv-cube'); return; }
        n++;
        add(h.note || L('ui.log_pass'), h.p, '', i, n + '. ' + h.dice.join('-'));
        const t = revTurn(i);
        if (t && t.cls !== 'ok' && t.cls !== 'forced') {
            const mk = document.createElement('span');
            mk.className = 'bg-mk bg-mk-' + t.cls;
            mk.textContent = { inaccuracy: '?!', mistake: '?', blunder: '??' }[t.cls] + ' −' + t.err.toFixed(2);
            mk.title = L('rev.cls_' + t.cls);
            // with the limit, the mistakes the review opens in full are the ones to tap first
            if (proLocked() && isErr(t) && turnOpen(t)) {
                mk.classList.add('bg-mk-open');
                mk.textContent += ' ★';
                mk.title += ' · ' + L('rev.open_mark');
            }
            frag.lastChild.appendChild(mk);
        }
    });
    list.appendChild(frag);
    // keep the newest line in view inside the list only (never the page); while a
    // past move is on the board — opened by «Review the game», walked by ‹ › — that line
    const box = $('bgMovesScroll');
    if (box && app.replay == null) box.scrollTop = box.scrollHeight;
    else if (box) {
        const on = list.querySelector('.bg-mv.on');
        if (on) {
            const r = on.getBoundingClientRect(), b = box.getBoundingClientRect();
            if (r.top < b.top) box.scrollTop -= b.top - r.top;
            else if (r.bottom > b.bottom) box.scrollTop += r.bottom - b.bottom;
        }
    }
    const cnt = $('bgMovesN'); if (cnt) cnt.textContent = n ? String(n) : '';
    renderReview();
    renderCoach();
}

/** The game as plain text: the header, the moves in the usual notation, the result. */
function gameRecord() {
    const gm = g();
    if (!gm) return '';
    const c = app.m.cfg;
    const lines = [];
    lines.push(L('setup.variant_' + c.variant) + ' · ' + (c.matchTo > 0 ? L('setup.match_n', { n: c.matchTo }) : L('setup.match_free')) + ' · ' + L('ui.game_no') + ' ' + app.m.gameNo);
    lines.push(L('ui.white') + ': ' + nameOf(0) + ' — ' + L('ui.black') + ': ' + nameOf(1) + (app.mode === 'bot' ? ' (' + L('ui.computer_' + app.level) + ')' : ''));
    for (const o of gm.opening) lines.push(L('ui.opening_line', { w: o[0], b: o[1] }));
    let n = 0;
    for (const h of gm.history) {
        const who = h.p === 0 ? L('ui.white') : L('ui.black');
        if (h.kind === 'double') lines.push(L('ui.log_double', { who, v: h.value }));
        else if (h.kind === 'take') lines.push(L('ui.log_take', { who, v: h.value }));
        else if (h.kind === 'drop') lines.push(L('ui.log_drop', { who }));
        else if (h.kind === 'resign') lines.push(L('ui.log_resign', { who }));
        else { n++; lines.push(n + '. ' + who + ' ' + h.dice.join('-') + ': ' + (h.note || L('ui.log_pass'))); }
    }
    if (gm.result) {
        const k = gm.result.kind.replace('resign-', '');
        lines.push(L('res.winner_is', { who: nameOf(gm.result.winner) }) + ' — ' + L('res.kind_' + (k === 'drop' ? 'drop' : k), { n: gm.result.points }));
    }
    lines.push(location.origin + '/' + (CFG.locale || 'en') + '/' + SLUG);
    return lines.join('\n');
}

/** The review of the game on the board, or null (a review of an earlier game does not count). */
function curReview() {
    return app.review && app.m && app.review.gameNo === app.m.gameNo ? app.review : null;
}

function revTurn(i) {
    const rev = curReview();
    return rev ? rev.turns.find((t) => t.i === i) : null;
}

const isErr = (t) => !!t && !t.bestSame && (t.cls === 'inaccuracy' || t.cls === 'mistake' || t.cls === 'blunder');
const humanSides = () => (app.mode === 'local' ? [0, 1] : [app.me]);

/*
 * The review teaches in full — the better move, its arrows and why — on the
 * person's FREE_BEST biggest mistakes of each game for everyone; on his other
 * moves it gives the verdict and the win chances, and the better move there
 * comes with Premium. Farid's call of 20.10.2026: a reader left the review as
 * «dry» (the better move was locked on every move — 1 visit to Premium from it
 * in 21 days, no payment, while the in-game hint shows the best move anyway).
 */
const FREE_BEST = 3;
let openMemo = { key: '', set: new Set() };
function openTurns() {
    const rev = curReview();
    if (!rev) return new Set();
    const key = rev.gameNo + ':' + rev.turns.length + ':' + app.mode + ':' + app.me;
    if (openMemo.key !== key) {
        const set = new Set();
        for (const p of humanSides()) {
            rev.turns.filter((t) => t.p === p && isErr(t)).sort((a, b) => b.err - a.err).slice(0, FREE_BEST).forEach((t) => set.add(t.i));
        }
        openMemo = { key, set };
    }
    return openMemo.set;
}
function turnOpen(t) { return !proLocked() || openTurns().has(t.i); }

/** «48 % instead of 61 %» — only when the win chances agree with the verdict. */
function winWords(t) {
    if (t.winP == null || t.winB == null) return '';
    const a = Math.round(t.winP * 100), b = Math.round(t.winB * 100);
    return b > a ? L('rev.win', { w1: a, w2: b }) : '';
}

function proLink() {
    const a = document.createElement('a');
    a.href = (CFG.pro && CFG.pro.url) || '#';
    a.className = 'bg-pro-link';
    a.textContent = L('pro.cta');
    a.addEventListener('click', () => feature('bg_pro_click', { from: 'review' }));
    return a;
}

/** The first move worth a look: the person's first mistake the review opens in
    full, else his first inaccuracy or worse, else his first move with a choice. */
function firstReviewMove() {
    const rev = curReview(), gm = g();
    if (!rev || !gm) return null;
    const his = (t) => humanSides().includes(t.p) && gm.history[t.i] && gm.history[t.i].moves;
    const t = rev.turns.find((x) => his(x) && isErr(x) && turnOpen(x)) || rev.turns.find((x) => his(x) && isErr(x))
        || rev.turns.find((x) => his(x) && !x.forced) || rev.turns.find(his);
    return t ? t.i : null;
}

/*
 * «What the better move would have done» — beside the move list while a move of
 * the game is on the board: the facts coach.js found (analysis puts them in
 * `why`), in words. Under the board stays the short verdict; here is the why.
 */
function renderCoach() {
    const box = $('bgCoach');
    if (!box) return;
    const t = app.replay != null ? revTurn(app.replay) : null;
    box.textContent = '';
    box.hidden = !isErr(t);
    if (box.hidden) return;
    const h = document.createElement('p');
    h.className = 'bg-coach-h';
    if (!turnOpen(t)) {
        h.textContent = L('rev.coach_locked') + ' ';
        h.appendChild(proLink());
        box.appendChild(h);
        return;
    }
    h.textContent = L('rev.why_title', { m: R.notate(t.best) });
    box.appendChild(h);
    const why = t.why && t.why.length ? t.why : null;
    for (const r of why || [{ k: null }]) {
        const p = document.createElement('p');
        p.className = 'bg-coach-why';
        p.textContent = r.k ? L('rev.why.' + r.k, r) : L('rev.why_none');
        box.appendChild(p);
    }
}

function renderStats() {
    const box = $('bgStats');
    if (!box) return;
    const m = app.m;
    const st = m ? m.stats : null;
    box.textContent = '';
    if (!st) return;
    // faces as bars against the expected share (1/6 of all dice of that side)
    const bars = document.createElement('div');
    bars.className = 'bg-bars';
    const max = Math.max(1, ...[0, 1].flatMap((p) => st.faces[p].slice(1)), (st.rolls[0] * 2) / 6, (st.rolls[1] * 2) / 6);
    for (let f = 1; f <= 6; f++) {
        const col = document.createElement('div'); col.className = 'bg-bar-col';
        const pair = document.createElement('div'); pair.className = 'bg-bar-pair';
        for (const p of [0, 1]) {
            const v = document.createElement('div');
            v.className = 'bg-bar-v ' + (p === 0 ? 'w' : 'b');
            v.style.height = Math.round((st.faces[p][f] / max) * 100) + '%';
            v.title = nameOf(p) + ': ' + st.faces[p][f];
            pair.appendChild(v);
        }
        const exp = document.createElement('div'); exp.className = 'bg-bar-exp';
        exp.style.bottom = Math.round((((st.rolls[0] + st.rolls[1]) * 2 / 12) / max) * 100) + '%';
        pair.appendChild(exp);
        const lab = document.createElement('div'); lab.className = 'bg-bar-f'; lab.textContent = String(f);
        col.appendChild(pair); col.appendChild(lab);
        bars.appendChild(col);
    }
    box.appendChild(bars);
    const lg = document.createElement('div');
    lg.className = 'bg-legend';
    lg.innerHTML = '';
    for (const p of [0, 1]) {
        const s = document.createElement('span');
        const i = document.createElement('i'); i.className = 'bg-bar-v ' + (p === 0 ? 'w' : 'b'); i.style.width = '10px';
        s.appendChild(i); s.appendChild(document.createTextNode(nameOf(p)));
        lg.appendChild(s);
    }
    const se = document.createElement('span'); se.textContent = '┄ ' + L('stats.expected'); lg.appendChild(se);
    box.appendChild(lg);

    const tbl = document.createElement('table');
    tbl.className = 'bg-stats';
    const head = document.createElement('tr');
    const th = (t) => { const e = document.createElement('th'); e.textContent = t; return e; };
    head.appendChild(th(''));
    head.appendChild(th(L('stats.rolls')));
    head.appendChild(th(L('stats.doubles')));
    head.appendChild(th(L('stats.pips')));
    tbl.appendChild(head);
    for (const p of [0, 1]) {
        const tr = document.createElement('tr');
        const nm = document.createElement('td');
        nm.className = 'bg-stats-name';
        nm.textContent = nameOf(p);
        tr.appendChild(nm);
        const rolls = st.rolls[p];
        const td = (t) => { const e = document.createElement('td'); e.textContent = t; tr.appendChild(e); };
        td(String(rolls));
        td(st.doubles[p] + ' / ' + (rolls / 6).toFixed(1));
        td(st.pips[p] + ' / ' + Math.round(rolls * 8.1667));
        tbl.appendChild(tr);
    }
    box.appendChild(tbl);
    const note = document.createElement('p');
    note.className = 'bg-note';
    note.textContent = L('stats.note_short');
    box.appendChild(note);
    renderFair();
}

let fairBusy = false;
function renderFair() {
    const box = $('bgFair');
    if (!box) return;
    box.textContent = '';
    if (!app.m) return;
    const h = document.createElement('h3');
    h.textContent = L('fair.title');
    box.appendChild(h);
    const p = (t, cls) => { const e = document.createElement('p'); e.className = 'bg-note' + (cls ? ' ' + cls : ''); e.style.margin = '6px 0 0'; e.textContent = t; box.appendChild(e); return e; };
    if (app.mode === 'online') { p(L('fair.online')); return; }
    const f = app.fair;
    if (!f || !f.commit) { p(L('fair.none')); return; }
    p(L('fair.how'));
    const c = document.createElement('p');
    c.className = 'bg-note';
    c.style.margin = '6px 0 0';
    c.append(L('fair.commit_label') + ' ');
    const code = document.createElement('code'); code.textContent = f.commit;
    c.appendChild(code);
    box.appendChild(c);
    p(L('fair.count', { n: f.log.length }));
    for (const v of (f.verified || []).slice(-3).reverse()) {
        const e = p(v.ok ? L('fair.ok', { n: v.n }) : L('fair.bad', { n: v.n }), v.ok ? 'bg-fair-ok' : 'bg-fair-bad');
        const det = document.createElement('code');
        det.textContent = ' seed ' + v.seed;
        det.style.display = 'block';
        e.appendChild(det);
    }
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'bg-btn bg-btn-wide';
    b.style.marginTop = '10px';
    b.id = 'bgFairCheck';
    b.textContent = L('fair.check_btn');
    b.disabled = fairBusy || !f.log.length || app.busy;
    b.addEventListener('click', checkFair);
    box.appendChild(b);
}

async function checkFair() {
    const f = app.fair;
    if (!f || !f.log.length || fairBusy) return;
    fairBusy = true;
    renderFair();
    try {
        const r = await verifySeries(f);
        const entry = { ok: r.ok, n: r.n, seed: f.seed, commit: f.commit };
        const verified = (f.verified || []).concat([entry]);
        await newFairSeries();
        app.fair.verified = verified;
        feature('bg_fair_check', { ok: r.ok ? 1 : 0, n: r.n });
        persist();
    } catch (err) {
        diag('fair_check_failed', { msg: String(err && err.message || err) });
    }
    fairBusy = false;
    renderFair();
}

function renderMatchCard() {
    const m = app.m;
    const gm = g();
    if (!m || !gm) return;
    const c = m.cfg;
    $('bgMatchH').textContent = L('setup.variant_' + c.variant) + ' · ' + (c.matchTo > 0 ? L('setup.match_n', { n: c.matchTo }) : L('setup.match_free'));
    const sc = $('bgScore');
    sc.textContent = '';
    for (const p of [0, 1]) {
        const row = document.createElement('div');
        row.className = 'bg-score-row' + (m.score[p] > m.score[1 - p] ? ' lead' : '') + (m.over && m.winner === p ? ' win' : '');
        const disc = document.createElement('span'); disc.className = 'bg-disc ' + (p === 0 ? 'bg-disc-w' : 'bg-disc-b');
        const nm = document.createElement('span'); nm.className = 'bg-score-nm'; nm.textContent = nameOf(p);
        const pts = document.createElement('b'); pts.className = 'bg-score-pts'; pts.textContent = String(m.score[p]);
        row.appendChild(disc); row.appendChild(nm); row.appendChild(pts);
        sc.appendChild(row);
    }
    const facts = $('bgFacts');
    facts.textContent = '';
    const fact = (k, v) => { const li = document.createElement('li'); const a = document.createElement('span'); a.textContent = k; const b = document.createElement('b'); b.textContent = v; li.appendChild(a); li.appendChild(b); facts.appendChild(li); };
    fact(L('setup.opponent'), app.mode === 'bot' ? L('ui.computer_' + app.level) : L('setup.mode_' + app.mode));
    fact(L('ui.game_no'), String(m.gameNo));
    if (G.cubeAvailable(m)) fact(L('ui.cube_now'), gm.cube.owner < 0 ? L('ui.cube_center', { v: gm.cube.value }) : L('ui.cube_owner', { v: gm.cube.value, who: nameOf(gm.cube.owner) }));
    if (gm.crawford) fact(L('ui.rule'), L('ui.crawford_game'));
    if (c.variant === R.LONG && c.koksLong) fact(L('ui.rule'), L('setup.opt_koks_short'));
    $('bgNet').hidden = app.mode !== 'online';
}

function renderTabs() {
    const has = !!app.m;
    $('bgTabDice').hidden = !has;
    if (!has && app.tab === 'dice') selectTab('game', true);
}

/* the panels are rebuilt when what they show changed, not on every render
   (a render comes with every step, every tick of the dice) */
const memo = {};
function changed(name, key) {
    if (memo[name] === key) return false;
    memo[name] = key;
    return true;
}

function render() {
    drawBoard();
    renderPlayers();
    renderActions();
    renderStatus();
    renderOverlay();
    renderTabs();
    const gm = g();
    if (app.m && app.tab === 'game') {
        const rv = app.review;
        if (changed('moves', [app.m.gameNo, gm.history.length, gm.opening.length, gm.phase, app.replay, rv ? rv.gameNo + ':' + rv.turns.length : '', app.reviewing, proLocked()].join('|'))) renderMoves();
    }
    if (app.tab === 'dice' && app.m) {
        const f = app.fair;
        if (changed('dice', [app.m.gameNo, app.m.stats.rolls.join(','), f ? f.commit + ':' + f.log.length + ':' + (f.verified || []).length : '', fairBusy, app.busy].join('|'))) renderStats();
    }
    if (app.m && changed('card', JSON.stringify([app.m.score, app.m.gameNo, app.m.over, gm.cube, gm.crawford, app.mode, app.level]))) renderMatchCard();
    updateMeter();
}

/* ─────────────────────────────── set-up UI ──────────────────────────────── */

function renderSetup() {
    // the rows, the chosen buttons and the captions: the same function that drew
    // them before the first paint (the pre-paint script of the blade)
    if (window.__bgApplySetup) window.__bgApplySetup(setup);
    const sum = [];
    if (setup.mode === 'bot') sum.push(L('setup.level_' + setup.level));
    sum.push(+setup.matchTo > 0 ? L('setup.match_n', { n: setup.matchTo }) : L('setup.match_free'));
    if (setup.cube && +setup.matchTo !== 1) sum.push(L('setup.cube'));
    if (setup.koksLong && setup.variant === 'long') sum.push(L('setup.opt_koks_short'));
    $('bgMoreSum').textContent = sum.join(' · ');
    if (!g()) { drawBoard(); renderPlayers(); renderStatus(); }
}

function bindSetup() {
    document.querySelectorAll('[data-bg-set]').forEach((b) => {
        b.addEventListener('click', () => {
            const [k, v] = b.getAttribute('data-bg-set').split(':');
            const before = setup[k];
            setup[k] = k === 'matchTo' ? +v : v;
            save('setup', setup);
            if (k === 'variant' && before !== setup[k] && !g()) morphPreview(before);
            // the nets are needed by the strong levels: fetch them while the person still reads the set-up
            if ((k === 'level' || k === 'variant') && setup.mode === 'bot' && (setup.level === 'hard' || setup.level === 'expert')) ensureNets(setup.variant);
            renderSetup();
            fitSoon();
        });
    });
    $('bgOptCube').addEventListener('change', (e) => { setup.cube = e.target.checked; save('setup', setup); renderSetup(); fitSoon(); });
    $('bgOptCrawford').addEventListener('change', (e) => { setup.crawford = e.target.checked; save('setup', setup); });
    $('bgOptKoks').addEventListener('change', (e) => { setup.koksLong = e.target.checked; save('setup', setup); });
    $('bgStart').addEventListener('click', () => { Snd.play('move'); startGame(); });
    $('bgMore').addEventListener('toggle', () => { $('bgMore').dataset.touched = '1'; fitSoon(); });
    const join = () => {
        const RC = window.__RoomConnect;
        const r = RC ? RC.validateCodeDetailed($('bgJoinCode').value) : { error: 'format' };
        if (r.error) { $('bgJoinErr').textContent = L('online.bad_code'); return; }
        $('bgJoinErr').textContent = '';
        try { history.replaceState(null, '', location.pathname + '?room=' + r.code); } catch (_) {}
        joinOnline(r.code);
    };
    $('bgJoinBtn').addEventListener('click', join);
    $('bgJoinCode').addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); join(); } });
}

/** Switching long ↔ short before a game: the checkers walk to the new set-up. */
function morphPreview(from) {
    if (!board || !settings.animate || reduceMotion()) return;
    const wrap = $('bgBoardWrap');
    // a cross-fade of the board is enough to read the change; the site
    // primitive skips it where View Transitions are missing or motion is reduced
    if (typeof window.__viewTransition === 'function') {
        window.__viewTransition(() => drawBoard());
    } else if (wrap && wrap.animate) {
        wrap.animate([{ opacity: 0.35 }, { opacity: 1 }], { duration: 260, easing: 'ease-out' });
    }
    void from;
}

/* ───────────────────────────── settings panel ───────────────────────────── */

function themeLocked(th) {
    const pro = CFG.pro || {};
    return (pro.themes || []).includes(th) && proLocked();
}

function applyTheme(preview) {
    const th = preview || settings.theme;
    $('bgApp').dataset.board = themeLocked(th) && !preview ? 'wood' : th;
    document.querySelectorAll('[data-bg-theme]').forEach((b) => {
        const t = b.getAttribute('data-bg-theme');
        const on = t === th;
        b.classList.toggle('on', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
        if (themeLocked(t)) b.dataset.locked = '1'; else delete b.dataset.locked;
    });
}

function renderSound() {
    const b = $('bgSound');
    if (b) b.setAttribute('aria-pressed', settings.sound ? 'true' : 'false');
}

function bindSettings() {
    document.querySelectorAll('[data-bg-opt]').forEach((inp) => {
        const k = inp.getAttribute('data-bg-opt');
        inp.checked = !!settings[k];
        inp.addEventListener('change', () => {
            settings[k] = inp.checked;
            save('settings', settings);
            if (k === 'autoConfirm' && settings.autoConfirm && app.turn && app.turn.done) { commitHuman(); return; }
            step();
        });
    });
    document.querySelectorAll('[data-bg-theme]').forEach((b) => b.addEventListener('click', () => {
        const th = b.getAttribute('data-bg-theme');
        const note = $('bgThemeNote');
        feature('bg_theme', { theme: th, locked: themeLocked(th) ? 1 : 0 });
        if (themeLocked(th)) {
            // a Premium board: shown on the board right away, kept with Premium
            applyTheme(th);
            note.hidden = false;
            note.textContent = L('pro.theme_note') + ' ';
            const a = document.createElement('a');
            a.href = (CFG.pro && CFG.pro.url) || '#';
            a.textContent = L('pro.cta');
            a.addEventListener('click', () => feature('bg_pro_click', { from: 'theme' }));
            note.appendChild(a);
            return;
        }
        note.hidden = true;
        settings.theme = th;
        save('settings', settings);
        applyTheme();
    }));
    $('bgSound').addEventListener('click', () => {
        settings.sound = !settings.sound;
        save('settings', settings);
        Snd.setEnabled(settings.sound);
        renderSound();
        if (settings.sound) Snd.play('move');
    });
    applyTheme();
    renderSound();
    // who has Premium is known only after the session bootstrap: a Premium board
    // chosen earlier comes back then, and the review's locks open
    const again = () => { applyTheme(); if (app.m) renderMoves(); drawBoard(); renderStatus(); };
    document.addEventListener('tier-bootstrap:ready', again);
    window.addEventListener('auth-ready', again);
}

/* ─────────────────────────────── tabs ───────────────────────────────────── */

function selectTab(k, quiet) {
    app.tab = k;
    document.querySelectorAll('.bg-tab').forEach((x) => {
        const on = x.getAttribute('data-tab') === k;
        x.classList.toggle('on', on);
        x.setAttribute('aria-selected', on ? 'true' : 'false');
        x.tabIndex = on ? 0 : -1;
    });
    document.querySelectorAll('.bg-panel').forEach((p) => { p.hidden = p.getAttribute('data-tab') !== k; });
    if (k === 'game' && app.m) { delete memo.moves; renderMoves(); }
    if (k === 'dice') { delete memo.dice; renderStats(); }
    if (!quiet) fitSoon();
}

function bindTabs() {
    const tabs = [...document.querySelectorAll('.bg-tab')];
    tabs.forEach((b) => b.addEventListener('click', () => selectTab(b.getAttribute('data-tab'))));
    // arrow keys move along the tab list (the ARIA tabs pattern)
    $('bgRail').querySelector('.bg-tabs').addEventListener('keydown', (e) => {
        if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
        const vis = tabs.filter((t) => !t.hidden);
        const i = vis.findIndex((t) => t.classList.contains('on'));
        const dir = (e.key === 'ArrowRight') !== (document.documentElement.dir === 'rtl') ? 1 : -1;
        const nx = vis[(i + dir + vis.length) % vis.length];
        selectTab(nx.getAttribute('data-tab'));
        nx.focus();
        e.preventDefault();
    });
    // the move list: a past position on the board
    $('bgMoves').addEventListener('click', (e) => {
        const li = e.target.closest('[data-i]');
        if (li) enterReplay(+li.dataset.i);
    });
    $('bgMoves').addEventListener('keydown', (e) => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        const li = e.target.closest('[data-i]');
        if (li) { enterReplay(+li.dataset.i); e.preventDefault(); }
    });
    $('bgPrev').addEventListener('click', () => goReplay(-1));
    $('bgFwd').addEventListener('click', () => goReplay(1));
    $('bgBack').addEventListener('click', () => { leaveReplay(); render(); });
    $('bgReviewBtn').addEventListener('click', () => openReview('rail'));
    $('bgCopyRec').addEventListener('click', () => {
        const txt = gameRecord();
        if (!txt) return;
        const done = () => toast(L('ui.copied_record'), 'success');
        feature('bg_copy_record', {});
        try { navigator.clipboard.writeText(txt).then(done, () => toast(txt.slice(0, 200), 'info')); } catch (_) { toast(txt.slice(0, 200), 'info'); }
    });
}

function canReplay() {
    const gm = g();
    if (!gm || app.busy) return false;
    if (gm.phase === 'over') return true;
    // only while the game waits for the person: never under a moving computer
    return isHuman(gm.turn) && (gm.phase === 'roll' || gm.phase === 'move');
}
function enterReplay(i) {
    const gm = g();
    if (!gm || !gm.history[i] || !gm.history[i].moves) return;
    if (!canReplay()) { toast(L('ui.replay_wait'), 'info'); return; }
    if (app.replay === i) { leaveReplay(); render(); return; }
    app.replay = i;
    feature('bg_replay', {});
    render();
}
function leaveReplay() { app.replay = null; }

/* ─────────────────────────────── controls ───────────────────────────────── */

function undoStep() {
    if (!app.turn || app.busy || !app.turn.played.length) return;
    app.turn.undo();
    app.sel = null; app.hint = null;
    setMsg('', '');
    persist();
    render();
}

function bindControls() {
    $('bgRoll').addEventListener('click', humanRoll);
    $('bgDouble').addEventListener('click', humanDouble);
    $('bgUndo').addEventListener('click', undoStep);
    $('bgDone').addEventListener('click', () => commitHuman());
    $('bgHint').addEventListener('click', hint);
    $('bgResign').addEventListener('click', resign);
    $('bgNext').addEventListener('click', () => { app.overlay = null; nextGame(false); });
    $('bgNew').addEventListener('click', () => {
        if (g() && g().phase !== 'over' && (g().history.length || g().phase === 'move')) {
            app.overlay = { kind: 'confirm', text: L('msg.new_q'), yes: () => { app.overlay = null; leaveGame(); } };
            render();
            return;
        }
        leaveGame();
    });
    $('bgFull').addEventListener('click', () => {
        const el = $('bgApp');
        if (document.fullscreenElement) { try { document.exitFullscreen().catch(() => {}); } catch (_) {} return; }
        if (window.__safeFullscreen) window.__safeFullscreen(el, { onDenied: () => toast(L('ui.fs_denied'), 'info') });
    });
    document.addEventListener('fullscreenchange', fitSoon);
    $('bgCopy').addEventListener('click', () => {
        const v = $('bgLink').value;
        const done = () => toast(L('online.copied'), 'success');
        try { navigator.clipboard.writeText(v).then(done, () => { $('bgLink').select(); document.execCommand('copy'); done(); }); }
        catch (_) { $('bgLink').select(); }
    });
    const sh = $('bgShare');
    if (sh) {
        sh.hidden = !navigator.share;
        sh.addEventListener('click', () => { try { navigator.share({ title: document.title, url: $('bgLink').value }).catch(() => {}); } catch (_) {} });
    }
    // the keyboard: Space/Enter throw or finish, Backspace undoes, H asks for a hint
    document.addEventListener('keydown', (e) => {
        if (e.defaultPrevented || e.altKey || e.metaKey || (e.ctrlKey && e.key.toLowerCase() !== 'z')) return;
        const t = e.target;
        if (t && (t.closest && (t.closest('input,textarea,select,button,[contenteditable],a,[role=tab],[role=button]') || t.id === 'bgBoard'))) return;
        const gm = g();
        if (!gm || ovOpen() || !stageOnScreen()) return;
        const k = e.key;
        if (app.replay != null) {
            if (k === 'ArrowLeft' || k === 'ArrowRight') { goReplay((k === 'ArrowRight') !== (document.documentElement.dir === 'rtl') ? 1 : -1); e.preventDefault(); }
            else if (k === 'Escape') { leaveReplay(); render(); e.preventDefault(); }
            return;
        }
        if (k === ' ' || k === 'Enter') {
            if (gm.phase === 'roll' && isHuman(gm.turn) && !app.busy) { humanRoll(); e.preventDefault(); }
            else if (gm.phase === 'move' && app.turn && app.turn.done && !app.busy) { commitHuman(); e.preventDefault(); }
        } else if (k === 'Backspace' || (e.ctrlKey && k.toLowerCase() === 'z')) {
            if (app.turn && app.turn.played.length) { undoStep(); e.preventDefault(); }
        } else if (k === 'h' || k === 'H' || k === 'р' || k === 'Р') {
            if (!$('bgHint').hidden) { hint(); e.preventDefault(); }
        }
    });
}

function stageOnScreen() {
    const r = $('bgStage').getBoundingClientRect();
    const vis = Math.min(r.bottom, innerHeight) - Math.max(r.top, 0);
    return vis > Math.min(r.height, innerHeight) * 0.5;
}

function leaveGame() {
    stopOnline(true);
    if (app.reviewing) { stopReview(); app.reviewing = false; }
    try { if (location.search) history.replaceState(null, '', location.pathname); } catch (_) {}
    app.m = null; app.turn = null; app.overlay = null; app.last = null; app.hint = null; app.mode = null; app.replay = null; app.fair = null; app.review = null; app.meter = null;
    setMsg('', '');
    dropSave();
    wake(false);
    showSetup();
    render();
}

async function hint() {
    const gm = g();
    if (!app.turn || app.busy || !gm) return;
    app.turn.reset();
    app.sel = null;
    app.busy = true;
    setMsg(L('msg.hint_thinking'), '');
    render();
    try {
        await ensureNets();
        const r = await botAsk({ kind: 'rank', pos: gm.pos, p: gm.turn, d1: gm.dice[0], d2: gm.dice[1], firstTurn: G.firstTurnOf(gm, gm.turn), oppFirstTurn: G.firstTurnOf(gm, 1 - gm.turn), budgetMs: botBudget() + 300, top: 3 });
        const best = r.plays[0];
        app.hint = best.moves;
        let txt = L('msg.hint', { n: R.notate(best.moves) });
        if (best.win != null) txt += ' ' + L('msg.hint_win', { w: Math.round(best.win * 100) });
        setMsg(txt, 'info');
        stage('hint', { engine: r.engine });
    } catch (_) {
        setMsg('', '');
    }
    app.busy = false;
    render();
}

/* ───────────────────────────── the win meter ────────────────────────────── */

/*
 * The computer's estimate of who is winning, drawn along the board's middle
 * bar (a setting, off by default: in a game against the computer it is its own
 * opinion). Asked when the position settles — a turn committed, a roll waiting.
 */
let meterKey = '';
function updateMeter() {
    const gm = g();
    if (!settings.meter || !gm || gm.phase === 'opening' || gm.phase === 'over') {
        if (app.meter != null) { app.meter = null; meterKey = ''; drawBoard(); }
        return;
    }
    const key = app.m.gameNo + ':' + gm.history.length + ':' + gm.phase;
    if (key === meterKey) return;
    meterKey = key;
    const side = gm.turn;
    ensureNets().then(() => botAsk({ kind: 'meter', pos: gm.pos, p: side, onRoll: true }, { timeoutMs: 8000 })).then((r) => {
        if (!r || r.win == null || meterKey !== key) return;
        app.meter = side === viewer() ? r.win : 1 - r.win;
        drawBoard();
    }, () => {});
}

/* ─────────────────────────────── the review ─────────────────────────────── */

function proLocked() {
    const pro = CFG.pro || {};
    if (!pro.offered) return false;
    try { return !(window.__isProNow && window.__isProNow(false)); } catch (_) { return true; }
}

/*
 * The review runs in a worker of its own: it is seconds of CPU, and the
 * computer's next move must not wait behind it. A new game while it runs
 * terminates it (the only way to stop a busy worker).
 */
let reviewWorker = null;
function askReview(msg, onProgress) {
    return new Promise((resolve, reject) => {
        let w;
        try {
            if (!CFG.workerUrl) throw new Error('no worker url');
            if (window.__moduleWorkerOk && !window.__moduleWorkerOk()) throw new Error('module_worker');
            w = new Worker(CFG.workerUrl, { type: 'module' });
        } catch (err) {
            // no module worker: the main thread (only on request — see canAutoReview)
            botAsk(msg, { timeoutMs: 120000, onProgress }).then(resolve, reject);
            return;
        }
        reviewWorker = w;
        let timer = 0;
        const touch = () => { clearTimeout(timer); timer = setTimeout(() => { stopReview(); reject(new Error('timeout')); }, 45000); };
        w.onmessage = (e) => {
            const d = e.data || {};
            if (d.diag) { diag('bg_' + d.diag, { url: d.url, msg: d.msg }); return; }
            if (d.id === 1) { w.postMessage(Object.assign({ id: 2 }, msg)); touch(); return; }
            if (d.progress != null && !d.done) { if (onProgress) onProgress(d.progress); touch(); return; }
            clearTimeout(timer);
            stopReview();
            if (d.error) reject(new Error(d.error)); else resolve(d);
        };
        w.onerror = (e) => {
            // handled here (runReview names the failure) — cancel, as above
            if (e && typeof e.preventDefault === 'function') e.preventDefault();
            clearTimeout(timer);
            stopReview();
            reject(new Error(String(e && e.message || 'worker')));
        };
        w.postMessage({ id: 1, kind: 'init', nets: CFG.nets || {}, variant: msg.game && msg.game.v });
        touch();
    });
}
function stopReview() {
    if (reviewWorker) { try { reviewWorker.terminate(); } catch (_) {} reviewWorker = null; }
}

/* the review starts by itself at the end of a game where the device can afford it */
function canAutoReview() {
    try { if (navigator.connection && navigator.connection.saveData) return false; } catch (_) {}
    if (worker === false) return false;
    return (navigator.hardwareConcurrency || 2) >= 4;
}

/*
 * «Review the game» — the result card's button and the rail's. The person asked
 * to SEE the review: the card goes at once, the status line says the review
 * runs, and once it is there the board opens the first move worth a look, with
 * its verdict under the board and ‹ › to walk the game. Until 20.10.2026 the
 * card's button only marked the card closed (no render), and when the review
 * had already run by itself — every game on a 4-core device — it returned at
 * once: the click did nothing at all (a reader's report with a screenshot; 26
 * of ~290 sessions with a review ever opened a move of it).
 */
function openReview(from) {
    const gm = g();
    if (!gm || gm.phase !== 'over') return;
    if (app.overlay && app.overlay.kind === 'result') app.overlay = { kind: 'closed' };
    // the last move's message («Computer: 2-2, 1/off») would hide «Reviewing the game…»
    setMsg('', '');
    feature('bg_review_open', { from, state: curReview() ? 'done' : app.reviewing ? 'running' : 'none' });
    runReview(false);
    render();
}

/* the review is there and the person asked for it (a move he opened himself meanwhile stays) */
function showReviewMove() {
    selectTab('game');
    const i = firstReviewMove();
    if (i != null && app.replay == null && canReplay()) { app.replay = i; feature('bg_replay', { from: 'review' }); }
    render();
}

async function runReview(quiet) {
    const gm = g();
    if (!gm || gm.phase !== 'over') return;
    if (app.reviewing) { if (!quiet) { app.reviewQuiet = false; selectTab('game'); renderReview(); } return; }
    if (curReview()) { if (!quiet) showReviewMove(); return; }
    app.reviewing = true;
    app.reviewQuiet = !!quiet;
    app.review = null;
    if (!quiet) selectTab('game');
    renderReview(0);
    feature('bg_review_start', { variant: gm.v, mode: app.mode, auto: quiet ? 1 : 0 });
    const t0 = performance.now();
    try {
        await ensureNets();
        const gameNo = app.m.gameNo;
        const r = await askReview({ kind: 'analyze', game: { v: gm.v, history: gm.history }, koksLong: !!app.m.cfg.koksLong }, (f) => renderReview(f));
        // a new game began while it ran: the report belongs to a game no longer on the board
        if (!app.m || app.m.gameNo !== gameNo || !g() || g().phase !== 'over') { app.reviewing = false; return; }
        app.review = Object.assign(r.report, { gameNo: app.m.gameNo });
        const mine = app.mode === 'local' ? null : app.review.sides[app.me];
        feature('bg_review_done', { ms: Math.round(performance.now() - t0), engine: app.review.engine, acc: mine ? mine.accuracy : undefined });
    } catch (err) {
        diag('review_failed', { msg: String(err && err.message || err) });
        toast(L('rev.failed'), 'error');
    }
    // asked for while it ran (the card, the rail) — show it; a quiet one only fills the card
    const asked = !app.reviewQuiet;
    app.reviewing = false;
    app.reviewQuiet = false;
    if (asked && curReview()) showReviewMove();
    else render();
}

function renderReview(progress) {
    const gm = g();
    const btn = $('bgReviewBtn'), prog = $('bgReviewProg'), sum = $('bgReviewSum');
    if (!btn) return;
    const rev = app.review && app.m && app.review.gameNo === app.m.gameNo ? app.review : null;
    btn.hidden = !(gm && gm.phase === 'over' && !rev && !app.reviewing);
    prog.hidden = !app.reviewing;
    if (app.reviewing && progress != null) $('bgReviewBar').style.width = Math.round(progress * 100) + '%';
    sum.hidden = !rev;
    sum.textContent = '';
    if (!rev) return;
    for (const p of [0, 1]) {
        const sd = rev.sides[p];
        const row = document.createElement('div'); row.className = 'bg-rev-side';
        const disc = document.createElement('span'); disc.className = 'bg-disc ' + (p === 0 ? 'bg-disc-w' : 'bg-disc-b');
        const nm = document.createElement('span'); nm.className = 'bg-rev-nm'; nm.textContent = nameOf(p);
        const acc = document.createElement('span'); acc.className = 'bg-rev-acc'; acc.textContent = sd.choices ? sd.accuracy + '%' : '—';
        const accS = document.createElement('small'); accS.textContent = L('rev.accuracy'); acc.appendChild(accS);
        const det = document.createElement('span'); det.className = 'bg-rev-det';
        det.textContent = L('rev.detail', { b: sd.blunder, m: sd.mistake, i: sd.inaccuracy, pr: sd.pr, luck: (sd.luck > 0 ? '+' : '') + sd.luck.toFixed(2) });
        row.appendChild(disc); row.appendChild(nm); row.appendChild(acc); row.appendChild(det);
        sum.appendChild(row);
    }
    // the lesson of the game: what most of the person's mistakes had in common (coach.js)
    for (const p of humanSides()) {
        const ls = rev.sides[p] && rev.sides[p].lesson;
        if (!ls) continue;
        const el = document.createElement('p'); el.className = 'bg-rev-lesson';
        const b = document.createElement('b');
        b.textContent = (app.mode === 'local' ? nameOf(p) + ' · ' : '') + L('rev.lesson_title') + ': ';
        el.appendChild(b);
        el.appendChild(document.createTextNode(L('rev.lesson.' + ls.k, { n: ls.n, of: ls.of })));
        sum.appendChild(el);
    }
    const note = document.createElement('p'); note.className = 'bg-rev-note';
    note.textContent = L(rev.engine === 'net' ? 'rev.note' : 'rev.note_heuristic');
    sum.appendChild(note);
}

/* ──────────────────── the board's callbacks (a person moves) ────────────── */

function onSelect(q) {
    if (!app.turn || app.busy) return;
    app.sel = q;
    if (q != null) {
        const d = app.turn.destinations(q);
        if (!d.length) { app.sel = null; setMsg(L('why.' + (app.turn.whyNot(q, null) || 'no_die')), 'warn'); }
        else setMsg('', '');
    }
    render();
}

function onMove(from, to) {
    if (!app.turn || app.busy) return;
    const d = app.turn.destinations(from).find((x) => x.to === to);
    if (!d) return;
    setMsg('', '');
    app.hint = null;
    playSteps(d.steps, false).then(persist);
}

/** A double click on a checker: the largest single step it has (bearing off first). */
function onAutoMove(q) {
    if (!app.turn || app.busy) return;
    const ds = app.turn.destinations(q);
    if (!ds.length) return;
    const singles = ds.filter((d) => d.steps.length === 1);
    const pool = singles.length ? singles : ds;
    pool.sort((a, b) => (b.to === R.OFF) - (a.to === R.OFF) || b.steps[0].d - a.steps[0].d);
    app.sel = null;
    setMsg('', '');
    app.hint = null;
    feature('bg_dblclick', {});
    playSteps(pool[0].steps, false).then(persist);
}

/** The dice are the «done» button of a finished turn, as on a real board. */
function onDiceTap() {
    const gm = g();
    if (!gm || app.replay != null || app.busy || ovOpen()) return;
    if (gm.phase === 'move' && app.turn && isHuman(gm.turn)) {
        if (app.turn.done) { feature('bg_dice_done', {}); commitHuman(); }
        else { setMsg(L('msg.dice_tap_early'), 'info'); render(); }
    }
}

function onIllegal(from, to) {
    if (!app.turn) return;
    const why = app.turn.whyNot(from, to);
    setMsg(L('why.' + (why || 'no_die')), 'warn');
    app.sel = null;
    render();
}

function onBoardTap(q) {
    const gm = g();
    if (!gm || app.replay != null) return;
    if (gm.phase === 'roll' && isHuman(gm.turn) && !app.busy && !ovOpen()) { humanRoll(); return; }
    // a tap on one of my own checkers that cannot move: say which rule holds it
    if (gm.phase === 'move' && app.turn && isHuman(gm.turn) && q != null && q !== R.OFF) {
        const pos = app.turn.position();
        if (R.countAt(pos, gm.turn, q) > 0) {
            const why = app.turn.whyNot(q, null);
            if (why) { setMsg(L('why.' + why), 'warn'); render(); }
        }
    }
}

/* ─────────────────────────────── wake lock ──────────────────────────────── */

// The site primitive (public/js/wake-lock.js) owns keeping the screen on: it
// verifies the lock is really held, repairs it after a hidden period and falls
// back to a silent video where the API is missing. A named hold, released when
// the game is left.
const WAKE = 'backgammon-game';
function wake(on) {
    const W = window.__wakeLock;
    if (!W || typeof W.hold !== 'function') return;
    try {
        if (on) { const p = W.hold(WAKE); if (p && p.catch) p.catch(() => {}); }
        else W.releaseHold(WAKE);
    } catch (_) {}
}

/* ─────────────────────────── fitting one screen ─────────────────────────── */

/*
 * The console is sized to the screen that is actually left: the small viewport
 * height (100svh) minus the site's fixed chrome at the top (banner, header —
 * found by what sits at the top edge). It is measured when the screen or the
 * state changes and kept otherwise (27.09.2026: the board moved when a status
 * line wrapped, a button showed, the cookie banner came or went).
 *
 * The board is the only elastic part: its width is chosen so that the strips,
 * the status line and the board together fill that height, capped by the width
 * the console has. In the tall layout before a game, the set-up under the board
 * must fit too, so the board gives way to it (down to 62 % of the width).
 */
let fitRaf = 0, fitKey = null;
function fitSoon() {
    if (fitRaf) return;
    fitRaf = requestAnimationFrame(() => { fitRaf = 0; fit(); });
}
/* The size of the board: window.__bgFit, the inline script right after the
   console in the blade — it runs before the first paint, so the page is drawn
   in its final layout from the start. The board's size depends only on the
   screen and on set-up vs game, never on the text and buttons around it. */
function fit() {
    const k = window.__bgFit ? window.__bgFit() : null;
    if (k !== fitKey) { fitKey = k; fitStatus($('bgStatus')); }
}

/* ─────────────────────────────── restoring ──────────────────────────────── */

function restore(s) {
    app.mode = s.mode;
    app.level = s.level || 'medium';
    app.me = s.me || 0;
    app.m = s.m;
    app.fair = s.fair && s.fair.seed ? s.fair : null;
    app.turn = null; app.overlay = null; app.sel = null; app.replay = null;
    app.firstMoveSent = true;
    const gm = g();
    if (gm.phase === 'move' && isHuman(gm.turn) && Array.isArray(s.partial) && s.partial.length) {
        app.turn = G.turnFor(app.m);
        for (const [from, d] of s.partial) if (!app.turn.play({ from, d })) break;
    }
    const last = gm.history.length ? gm.history[gm.history.length - 1] : null;
    if (last && last.moves) { app.last = last.moves; app.lastBy = last.p; }
    stage('resumed', { mode: app.mode, variant: app.m.cfg.variant });
    ensureNets();
    showGameUi();
    if (app.mode === 'online' && s.net && s.net.code) {
        app.netCode = s.net.code;
        app.netRole = s.net.role;
        loadOnline().then((O) => {
            if (app.mode !== 'online' || app.net) return;
            app.net = O.create(netCallbacks());
            if (s.net.role === 'host') { app.net.host(s.net.code); showLink(s.net.code); }
            else app.net.join(s.net.code);
        }, netUnavailable);
    }
    if (gm.phase === 'over') app.overlay = { kind: 'closed' };
    step();
}

/* ─────────────────────────────── start-up ───────────────────────────────── */

function init() {
    const svg = $('bgBoard');
    board = createBoard(svg, {
        onSelect, onMove, onIllegal, onBoardTap, onAutoMove, onDiceTap,
        labels: { point: (n) => L('ui.point', { n }), bar: L('ui.bar'), off: L('ui.tray') },
    });
    bindSetup();
    bindSettings();
    bindTabs();
    bindControls();
    stage('board_ready', {});
    try {
        const ro = new ResizeObserver(fitSoon);
        ro.observe($('bgApp'));
    } catch (_) {}
    window.addEventListener('resize', fitSoon, { passive: true });
    window.addEventListener('orientationchange', fitSoon, { passive: true });
    // the banner of the site can close after load: measure again then
    setTimeout(fitSoon, 1200);
    setTimeout(fitSoon, 4000);

    const qs = new URLSearchParams(location.search);
    const room = qs.get('room');
    const hostCode = qs.get('host');
    const saved = readSave();
    const RC = window.__RoomConnect;
    if (room && RC) {
        const r = RC.validateCodeDetailed(room);
        if (!r.error) {
            setup.mode = 'online';
            const resume = saved && saved.mode === 'online' && saved.net && saved.net.code === r.code && saved.net.role === 'guest' ? saved.m : null;
            joinOnline(r.code, resume);
            if (resume) {
                const last = resume.game.history[resume.game.history.length - 1];
                if (last && last.moves) { app.last = last.moves; app.lastBy = last.p; }
            }
            render();
            fit();
            return;
        }
    }
    if (saved && saved.mode === 'online') {
        const mine = saved.net && saved.net.role === 'host' && hostCode === saved.net.code && RC && RC.isMyHostedRoom(G.ROOM_PREFIX, saved.net.code);
        if (mine) { restore(saved); fit(); return; }
        dropSave();
    } else if (saved && saved.m.game.phase !== 'over') {
        restore(saved);
        fit();
        return;
    }
    try { if (hostCode) history.replaceState(null, '', location.pathname); } catch (_) {}
    showSetup();
    render();
    fit();
}

function idle(fn) {
    if ('requestIdleCallback' in window) window.requestIdleCallback(fn, { timeout: 4000 });
    else setTimeout(fn, 1500);
}

// warm the sound module (and the nets of a strong computer) while the person reads the set-up
function warmSound() {
    // the audio context too, in a later idle slot: its first creation blocks the thread (sound.js prewarm)
    if (settings.sound) loadSound().then((m) => { if (m && m.prewarm) idle(() => m.prewarm()); });
    if (setup.mode === 'bot' && (setup.level === 'hard' || setup.level === 'expert')) ensureNets(setup.variant);
}
if ('requestIdleCallback' in window) window.requestIdleCallback(warmSound, { timeout: 4000 });
else setTimeout(warmSound, 2500);

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
else init();

/* for the e2e scripts: read-only view of the state */
window.__bgDebug = {
    get phase() { const x = g(); return x ? x.phase : null; },
    get turn() { const x = g(); return x ? x.turn : null; },
    get mode() { return app.mode; },
    get busy() { return app.busy; },
    get pos() { const x = g(); return x ? (app.turn ? app.turn.position() : x.pos) : null; },
    get dice() { const x = g(); return x ? x.dice : null; },
    get score() { return app.m ? app.m.score.slice() : null; },
    get history() { const x = g(); return x ? x.history.length : 0; },
    get played() { return app.turn ? app.turn.played.length : 0; },
    get maxUsed() { return app.turn ? app.turn.maxUsed : null; },
    get fair() { return app.fair ? { commit: app.fair.commit, n: app.fair.log.length, verified: (app.fair.verified || []).length } : null; },
    get replay() { return app.replay; },
    get engine() { return app.lastEngine || null; },
    get meter() { return app.meter; },
    get nets() { return app.nets || null; },
    ask(msg) { return botAsk(msg); },
    get review() { return app.review ? { engine: app.review.engine, turns: app.review.turns.length, sides: app.review.sides } : null; },
    get firstReviewMove() { return firstReviewMove(); },
    reviewTurns() { const rev = curReview(); return rev ? rev.turns.map((t) => ({ i: t.i, p: t.p, cls: t.cls, err: t.err, why: t.why || null, winP: t.winP, winB: t.winB, err1: isErr(t), open: turnOpen(t) })) : []; },
    get proLocked() { return proLocked(); },
    get layout() { return document.getElementById('bgApp').dataset.layout; },
    legal() { return app.turn ? app.turn.legalSteps() : []; },
    dests(q) { return app.turn ? app.turn.destinations(q).map((d) => d.to) : []; },
    xy(p, q) { return board.clientXY(p, q); },
    diceXY() { return board.diceXY(); },
};
